import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  Fragment,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import digisakaGptLogo from '../../../assets/pandoy.png';
import { CLOSE_PANDOY_EVENT, OPEN_PANDOY_EVENT, type PandoyOpenMode } from '../utils/pandoyEvents';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import { digisakaAuthRequestInit, gptInferenceJsonHeaders } from '../../../config/digisakaGptInference';
import {
  DIGISAKA_GPT_CONVERSATION_STORAGE_KEY,
  createDigisakaGptConversation,
  deleteDigisakaGptConversation,
  deleteDigisakaGptMemory,
  fetchDigisakaGptMessages,
  listDigisakaGptConversations,
  listDigisakaGptMemories,
  renameDigisakaGptConversation,
  updateDigisakaGptConversationFlags,
  type GptConversationSummary,
  type GptMemoryItem,
  type StoredGptMessageRow,
} from '../../../services/digisakaGptConversations';
import { applyComputeMsFromHeaderValue } from '../../../utils/inferenceComputeBudget';
import {
  buildDashboardScreenshotToolResult,
  captureDashboardScreenshot,
  CAPTURE_DASHBOARD_SCREENSHOT_TOOL,
} from '../utils/dashboardScreenshot';
import { useTheme } from '../theme/ThemeContext';
import { BRAND_GREEN, getPandoyTheme, getSidebarTheme } from '../theme/themeStyles';
import { useSpeechToText } from '../utils/hooks/useSpeechToText';
import { useTextToSpeech } from '../utils/hooks/useTextToSpeech';
import i18n from '../../../i18n';
import { useAuth } from '../../user-dashboard/auth/AuthContext';
import {
  pandoyPlaceholder,
  pandoyFieldStarters,
  pandoyRoleStarters,
  type PandoyStarter,
} from '../../../content/pandoyAudience';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  imageUrl?: string | null;
  /** When true, newly received assistant text reveals with a fast typing animation. */
  revealTyping?: boolean;
  /** True while Kimi token deltas are still arriving for this message. */
  isStreaming?: boolean;
  /** Optional follow-up questions parsed from the assistant response. */
  suggestedQuestions?: string[];
  /** One-line conclusion shown in the green summary bar. */
  takeaway?: string;
}

interface AutoRequest {
  source:
    | 'riceForecast'
    | 'heatMap'
    | 'farmWalk'
    | 'timeSeries'
    | 'cropCalendar'
    | 'climate'
    | 'strawBurn'
    | 'treeDetection'
    | 'crownSegmentation'
    | 'cropfieldDelineation'
    | 'bsri'
    | 'plantingMethod'
    | 'other';
  payload?: any;
  /** Open chat and fill the composer (do not auto-send). */
  pasteText?: string;
}

/** Selected map field passed from the dashboard (saved farms only). */
export interface GptFieldContext {
  farmId: string;
  farmName?: string;
  /** Separate polygons on this farm. More than 1 means compare each block. */
  blockCount?: number;
}

export function isValidGptFieldContext(
  field: { farm_id?: string | number; isDemo?: boolean } | null | undefined
): field is { farm_id: string | number; farm_name?: string; isDemo?: boolean } {
  if (!field?.farm_id) return false;
  const id = String(field.farm_id);
  if (field.isDemo || id === 'demo_field' || id.startsWith('uploaded_field_')) return false;
  return true;
}

interface DigisakaLLMChatProps {
  autoRequest?: AutoRequest | null;
  /** Map-only capture target (layer analysis offers). */
  screenshotTargetRef?: React.RefObject<HTMLDivElement | null>;
  /** Full dashboard shell for auto screenshot on “analyze current view” questions. */
  dashboardScreenshotRef?: React.RefObject<HTMLDivElement | null>;
  onAutoRequestConsumed?: () => void;
  /** When the user has a saved field selected on the map, show tool quick actions. */
  fieldContext?: GptFieldContext | null;
  /** Fired when chat enters or exits full-screen so the host can adjust layout (e.g. hide sidebar). */
  onChatFullscreenChange?: (isFullscreen: boolean) => void;
  /** True while Pandoy fills the main area, the same way Settings does. */
  onPandoyPageChange?: (open: boolean) => void;
  /** Hide the corner launcher and the small popup (Settings or the Pandoy page is open). */
  hideCornerLauncher?: boolean;
}

const PANDOY_AI_DISPLAY_NAME = 'Pandoy AI';
const GPT_DISCLAIMER_TEXT =
  'Pandoy AI can make mistakes. Please verify important information.';
/** Delay before Pandoy auto-pops open (analysis offer or map idle). */
const PANDOY_AUTO_POP_DELAY_MS = 10_000;
const PANDOY_AUTO_EXPAND_DISMISSED_KEY = 'digisaka-pandoy-auto-expand-dismissed';

function isPandoyAutoExpandDismissed(): boolean {
  try {
    return sessionStorage.getItem(PANDOY_AUTO_EXPAND_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function markPandoyAutoExpandDismissed(): void {
  try {
    sessionStorage.setItem(PANDOY_AUTO_EXPAND_DISMISSED_KEY, '1');
  } catch {
    /* ignore storage errors */
  }
}

interface ToolStatus {
  tool: string;
  label: string;
  done: boolean;
}

const FARMER_TOOL_LABELS: Record<string, string> = {
  get_field_health: 'Checking how green and moist the field looks…',
  get_current_weather: 'Checking today’s weather at the field…',
  get_weather_forecast: 'Checking the next few days of weather…',
  get_vegetation_history: 'Looking at how the crop has changed over time…',
  get_anomaly: 'Checking if anything looks unusual…',
  get_climate_history: 'Looking up past rain and temperature…',
  check_insurance: 'Gathering field notes for insurance…',
  get_farm_cadi_drought: 'Checking the drought outlook…',
  get_farm_pest_risk: 'Checking pest risk for this field…',
  analyze_crop_calendar: 'Checking the crop calendar and growth stage…',
  get_location_info: 'Finding where this field is…',
  get_digisaka_farm_count: 'Counting Digisaka farms…',
  get_digisaka_farm_info: 'Looking up this farm’s details…',
  list_digisaka_farm_provinces: 'Checking farms by province…',
  list_my_farms: 'Loading your farms from Digisaka…',
  remember_about_user: 'Saving that for later chats…',
  forget_about_user: 'Removing that from memory…',
  capture_dashboard_screenshot: 'Looking at what’s on your screen…',
};

function farmerToolLabel(tool: string, label?: string): string {
  const raw = (label || '').trim();
  if (raw && !/^Running\s/i.test(raw) && !/^[a-z][a-z0-9_]*[.…]*$/i.test(raw)) {
    return raw;
  }
  const key = tool.trim();
  if (key && FARMER_TOOL_LABELS[key]) return FARMER_TOOL_LABELS[key];
  return 'Checking your field…';
}

function uiPartsFromStoredContent(content: unknown): { text: string; imageUrl?: string | null } {
  if (typeof content === 'string') return { text: content };
  if (Array.isArray(content)) {
    const parts = content as any[];
    let text = '';
    let imageUrl: string | null = null;
    for (const part of parts) {
      if (part?.type === 'text' && typeof part.text === 'string') text += part.text;
      if (
        part?.type === 'image_url' &&
        part.image_url &&
        typeof part.image_url.url === 'string'
      ) {
        imageUrl = part.image_url.url;
      }
    }
    return { text: text.trim(), imageUrl };
  }
  if (content != null && typeof content === 'object') return { text: JSON.stringify(content) };
  return { text: '' };
}

const SUGGESTED_QUESTIONS_MARKER = '---SUGGESTED_QUESTIONS---';
const TAKEAWAY_MARKER = '---TAKEAWAY---';

function hideMachineTail(text: string): string {
  let cut = text.length;
  for (const marker of [TAKEAWAY_MARKER, SUGGESTED_QUESTIONS_MARKER]) {
    const index = text.indexOf(marker);
    if (index !== -1) cut = Math.min(cut, index);
  }
  const partial = text.match(/---[A-Z_]*$/);
  if (partial?.index != null) cut = Math.min(cut, partial.index);
  return text.slice(0, cut).trimEnd();
}

function stripEmojisFromText(text: string): string {
  return text
    .replace(/\p{Extended_Pictographic}/gu, '')
    .replace(/[\u200d\ufe0f]/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

function parseAssistantDisplayContent(rawText: string): {
  text: string;
  suggestedQuestions: string[];
  takeaway?: string;
} {
  let text = stripEmojisFromText(rawText);
  let suggestedQuestions: string[] = [];
  let takeaway: string | undefined;
  const markerIdx = text.indexOf(SUGGESTED_QUESTIONS_MARKER);
  if (markerIdx !== -1) {
    const block = text.slice(markerIdx + SUGGESTED_QUESTIONS_MARKER.length).trim();
    text = text.slice(0, markerIdx).trim();
    try {
      const parsed = JSON.parse(block);
      if (Array.isArray(parsed)) {
        suggestedQuestions = parsed
          .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
          .map((item) => item.trim())
          .slice(0, 3);
      }
    } catch {
      // Ignore malformed suggestion blocks from older chats.
    }
  }
  const takeawayIdx = text.indexOf(TAKEAWAY_MARKER);
  if (takeawayIdx !== -1) {
    const line = text
      .slice(takeawayIdx + TAKEAWAY_MARKER.length)
      .split('\n')
      .map((part) => part.trim())
      .find(Boolean);
    text = text.slice(0, takeawayIdx).trim();
    if (line) takeaway = line.replace(/^["']|["']$/g, '').slice(0, 180);
  }
  return { text, suggestedQuestions, takeaway };
}

function lastAssistantIdFromMessages(rows: Message[]): string | null {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (!rows[i].isUser) return rows[i].id;
  }
  return null;
}

function apiStoredRowToMessage(row: StoredGptMessageRow): Message {
  const isUser = row.role === 'user';
  const { text, imageUrl } = uiPartsFromStoredContent(row.content);
  const parsed = isUser
    ? { text, suggestedQuestions: [] as string[], takeaway: undefined as string | undefined }
    : parseAssistantDisplayContent(text);
  return {
    id: row.id,
    text: parsed.text,
    isUser,
    timestamp: row.created_at ? new Date(row.created_at) : new Date(),
    imageUrl: imageUrl ?? undefined,
    suggestedQuestions: parsed.suggestedQuestions.length ? parsed.suggestedQuestions : undefined,
    takeaway: parsed.takeaway,
  };
}

function extractAssistantReplyFromResponseData(
  responseData: any,
  fallbackAttachedImage?: string | null
): { text: string; imageUrl?: string | null; suggestedQuestions?: string[]; takeaway?: string } {
  let replyText = '';
  let replyImageUrl: string | null = null;
  const content = responseData?.choices?.[0]?.message?.content;
  if (Array.isArray(content)) {
    const textParts: string[] = [];
    content.forEach((part: any) => {
      if (typeof part === 'string') {
        textParts.push(part);
      } else if (part?.type === 'text' && typeof part.text === 'string') {
        textParts.push(part.text);
      } else if (
        part?.type === 'image_url' &&
        part.image_url &&
        typeof part.image_url.url === 'string'
      ) {
        if (!replyImageUrl) replyImageUrl = part.image_url.url;
      }
    });
    replyText = textParts.join('\n').trim() || `No response received from ${PANDOY_AI_DISPLAY_NAME}.`;
  } else if (typeof content === 'string') {
    replyText = content;
  } else if (content != null) {
    replyText = JSON.stringify(content);
  } else {
    replyText = `No response received from ${PANDOY_AI_DISPLAY_NAME}.`;
  }
  if (!replyImageUrl && fallbackAttachedImage) replyImageUrl = fallbackAttachedImage;

  const apiSuggestions = Array.isArray(responseData?.suggested_questions)
    ? responseData.suggested_questions
    : null;
  const parsed = parseAssistantDisplayContent(replyText);
  const suggestedQuestions =
    apiSuggestions && apiSuggestions.length
      ? apiSuggestions
          .filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0)
          .map((item: string) => item.trim())
          .slice(0, 3)
      : parsed.suggestedQuestions;

  return {
    text: parsed.text,
    imageUrl: replyImageUrl,
    suggestedQuestions: suggestedQuestions.length ? suggestedQuestions : undefined,
    takeaway: parsed.takeaway,
  };
}

const FAST_TYPING_TARGET_MS = 1800;
const FAST_TYPING_TICK_MS = 12;

function useFastTypingReveal(
  fullText: string,
  enabled: boolean,
  onTick?: () => void
): { displayedText: string; isTyping: boolean } {
  const [displayedLength, setDisplayedLength] = useState(() => (enabled ? 0 : fullText.length));
  const [isTyping, setIsTyping] = useState(enabled && fullText.length > 0);
  const onTickRef = useRef(onTick);

  useEffect(() => {
    onTickRef.current = onTick;
  }, [onTick]);

  useEffect(() => {
    onTickRef.current?.();
  }, [displayedLength]);

  useEffect(() => {
    if (!enabled) {
      setDisplayedLength(fullText.length);
      setIsTyping(false);
      return;
    }

    setDisplayedLength(0);
    setIsTyping(fullText.length > 0);

    if (!fullText.length) return;

    const ticks = Math.max(1, Math.ceil(FAST_TYPING_TARGET_MS / FAST_TYPING_TICK_MS));
    const charsPerTick = Math.max(3, Math.ceil(fullText.length / ticks));
    let current = 0;

    const intervalId = window.setInterval(() => {
      current = Math.min(fullText.length, current + charsPerTick);
      setDisplayedLength(current);
      if (current >= fullText.length) {
        setIsTyping(false);
        window.clearInterval(intervalId);
      }
    }, FAST_TYPING_TICK_MS);

    return () => window.clearInterval(intervalId);
  }, [enabled, fullText]);

  return {
    displayedText: fullText.slice(0, displayedLength),
    isTyping,
  };
}

function gptMarkdownProseClass(isDark: boolean): string {
  return `prose prose-sm max-w-none text-sm leading-relaxed prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 ${
    isDark
      ? 'prose-invert prose-strong:text-[#e6edf3] prose-headings:text-[#e6edf3] prose-p:text-[#c9d1d9] prose-li:text-[#c9d1d9]'
      : 'prose-strong:text-[#111827] prose-headings:text-[#008540] prose-p:text-[#111827] prose-li:text-[#111827]'
  } prose-h3:text-sm prose-h3:mt-3 prose-h3:mb-1 prose-h4:text-sm prose-h4:mt-2.5 prose-h4:mb-1`;
}

function gptMarkdownComponents(isDark: boolean) {
  return {
    table: ({ children, ...props }: any) => (
      <div
        className={`overflow-x-auto my-2 rounded-lg border ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}
      >
        <table className="min-w-full text-xs border-collapse" {...props}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }: any) => (
      <thead
        className={isDark ? 'bg-[#1c2432] text-[#c9d1d9]' : 'bg-[#f3f4f6] text-[#008540]'}
        {...props}
      >
        {children}
      </thead>
    ),
    th: ({ children, ...props }: any) => (
      <th
        className={`px-2 py-1.5 text-left text-[11px] font-semibold border-b whitespace-nowrap ${
          isDark ? 'border-gray-600' : 'border-gray-200'
        }`}
        {...props}
      >
        {children}
      </th>
    ),
    td: ({ children, ...props }: any) => (
      <td
        className={`px-2 py-1 text-[11px] border-b ${
          isDark ? 'border-gray-700' : 'border-gray-100'
        }`}
        {...props}
      >
        {children}
      </td>
    ),
    tr: ({ children, ...props }: any) => (
      <tr
        className={
          isDark ? 'even:bg-white/[0.04] hover:bg-white/[0.06]' : 'even:bg-[#f3f4f6] hover:bg-[#e5e7eb]/60'
        }
        {...props}
      >
        {children}
      </tr>
    ),
    h2: ({ children, ...props }: any) => (
      <h2
        className={`text-sm font-bold mt-3 mb-1 flex items-center gap-1 ${
          isDark ? 'text-[#e6edf3]' : 'text-[#008540]'
        }`}
        {...props}
      >
        {children}
      </h2>
    ),
    h3: ({ children, ...props }: any) => (
      <h3
        className={`text-[13px] font-semibold mt-2.5 mb-1 ${
          isDark ? 'text-[#c9d1d9]' : 'text-[#008540]'
        }`}
        {...props}
      >
        {children}
      </h3>
    ),
    blockquote: ({ children, ...props }: any) => (
      <blockquote
        className={`border-l-3 pl-3 pr-2 py-1.5 my-2 rounded-r-md text-xs ${
          isDark
            ? 'border-gray-500 bg-gray-700/60 text-gray-200'
            : 'border-[#008540] bg-[#f3f4f6] text-[#374151]'
        }`}
        {...props}
      >
        {children}
      </blockquote>
    ),
    code: ({ children, className, ...props }: any) => {
      const isInline = !className;
      return isInline ? (
        <code
          className={`rounded px-1 py-0.5 text-[11px] font-mono ${
            isDark ? 'bg-[#1c2432] text-[#c9d1d9]' : 'bg-[#f3f4f6] text-[#008540]'
          }`}
          {...props}
        >
          {children}
        </code>
      ) : (
        <code className={`${className || ''} text-[11px]`} {...props}>
          {children}
        </code>
      );
    },
    hr: (props: any) => (
      <hr className={`my-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`} {...props} />
    ),
  };
}

function GptAssistantMarkdown({
  text,
  revealTyping,
  isStreaming,
  isDark,
  onTypingTick,
  onTypingComplete,
}: {
  text: string;
  revealTyping?: boolean;
  /** Live Kimi token stream — show caret, skip fake typing reveal. */
  isStreaming?: boolean;
  isDark: boolean;
  onTypingTick?: () => void;
  onTypingComplete?: () => void;
}) {
  const { displayedText, isTyping } = useFastTypingReveal(
    text,
    !!revealTyping && !isStreaming,
    onTypingTick
  );
  const onTypingCompleteRef = useRef(onTypingComplete);

  useEffect(() => {
    onTypingCompleteRef.current = onTypingComplete;
  }, [onTypingComplete]);

  useEffect(() => {
    if (!isTyping && !isStreaming) onTypingCompleteRef.current?.();
  }, [isTyping, isStreaming]);

  useEffect(() => {
    if (isStreaming) onTypingTick?.();
  }, [text, isStreaming, onTypingTick]);

  return (
    <div className={gptMarkdownProseClass(isDark)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={gptMarkdownComponents(isDark)}>
        {isStreaming ? hideMachineTail(text) : hideMachineTail(displayedText)}
      </ReactMarkdown>
      {(isTyping || isStreaming) && (
        <span
          className={`inline-block w-[2px] h-[0.9em] ml-0.5 align-[-0.1em] animate-pulse ${
            isDark ? 'bg-[#c9d1d9]' : 'bg-[#008540]'
          }`}
          aria-hidden
        />
      )}
    </div>
  );
}

function gptPillsSectionShellClass(isDark: boolean): string {
  return `max-w-[88%] w-full rounded-[14px] border px-4 py-3 ${
    isDark ? 'border-[#2d3748] bg-[#1c2432] text-[#c9d1d9]' : 'border-[#e5e7eb] bg-white text-[#374151]'
  }`;
}

function gptPillButtonClass(isDark: boolean, disabled?: boolean): string {
  return `group w-full text-left text-sm leading-snug rounded-full border px-4 py-2.5 transition-colors ${
    disabled
      ? 'opacity-50 cursor-not-allowed'
      : isDark
        ? 'border-[#2d3748] bg-[#161b22] text-[#c9d1d9] hover:border-[#34d399]/40 hover:text-[#34d399]'
        : 'border-[#e5e7eb] bg-[#f3f4f6] text-[#374151] hover:border-[#008540]/50 hover:text-[#008540]'
  }`;
}

function GptChatPillsSection({
  title,
  iconClass = 'ri-question-line',
  isDark,
  children,
}: {
  title: string;
  iconClass?: string;
  isDark: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex justify-start">
      <div className={gptPillsSectionShellClass(isDark)}>
        <div className="mb-2.5 flex items-center gap-2 border-b border-dashed pb-2.5 border-inherit">
          <span
            className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
              isDark ? 'bg-[rgba(52,211,153,.16)] text-[#34d399]' : 'bg-[#00854014] text-[#008540]'
            }`}
            aria-hidden
          >
            <i className={iconClass} />
          </span>
          <p className={`text-sm font-medium ${isDark ? 'text-[#e6edf3]' : 'text-[#111827]'}`}>{title}</p>
        </div>
        <div className="flex flex-col gap-2">{children}</div>
      </div>
    </div>
  );
}

function GptSuggestedQuestionsCard({
  questions,
  isDark,
  disabled,
  onSelect,
}: {
  questions: string[];
  isDark: boolean;
  disabled?: boolean;
  onSelect: (question: string) => void;
}) {
  if (!questions.length) return null;

  return (
    <div className="mt-2">
      <GptChatPillsSection title="Related questions you might ask" isDark={isDark}>
        {questions.map((question) => (
          <button
            key={question}
            type="button"
            disabled={disabled}
            title="Click to ask this question"
            onClick={() => onSelect(question)}
            className={gptPillButtonClass(isDark, disabled)}
          >
            {question}
          </button>
        ))}
      </GptChatPillsSection>
    </div>
  );
}

/** Split buffered SSE wire format into complete frames (terminated by blank line). */
function consumeCompleteSseFrames(buffer: string): { rest: string; frames: { event: string; data: string }[] } {
  const frames: { event: string; data: string }[] = [];
  let rest = buffer;

  while (true) {
    const rnIdx = rest.indexOf('\r\n\r\n');
    const nIdx = rest.indexOf('\n\n');
    let sep = -1;
    let sepLen = 2;
    if (rnIdx !== -1 && (nIdx === -1 || rnIdx <= nIdx)) {
      sep = rnIdx;
      sepLen = 4;
    } else if (nIdx !== -1) {
      sep = nIdx;
      sepLen = 2;
    }
    if (sep === -1) break;

    const rawFrame = rest.slice(0, sep);
    rest = rest.slice(sep + sepLen);

    let eventName = 'message';
    const dataLines: string[] = [];
    for (const line of rawFrame.split(/\r?\n/)) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.startsWith('data: ') ? line.slice(6) : line.slice(5));
      }
    }
    frames.push({ event: eventName, data: dataLines.join('\n') });
  }

  return { rest, frames };
}

interface ClientCaptureRequest {
  tool: string;
  toolCallId: string;
  assistantMessage: Record<string, unknown>;
}

async function callDigisakaGptSSE(options: {
  conversationId: string | null;
  /** Full history when conversationId is null; otherwise only new user turn(s). */
  messages: { role: 'user' | 'assistant'; content: any }[];
  /** Active map field — server injects into agent context when set. */
  farmId?: string | null;
  /** When true, run in Instant mode (thinking disabled). */
  thinkingDisabled?: boolean;
  /** Resume agent after client-side dashboard screenshot tool. */
  agentResume?: boolean;
  resumeMessages?: Record<string, unknown>[];
  /** Called for each Kimi token delta before the final answer event. */
  onAnswerDelta?: (delta: string) => void;
  /** Discard a partial streamed draft when a tool round begins. */
  onStreamReset?: () => void;
  /** Called as soon as the answer SSE event arrives (before the stream closes). */
  onAnswer?: (data: any) => void;
  /** Functional updates so rapid tool_start/tool_done events reliably reach React state. */
  setToolStatuses?: Dispatch<SetStateAction<ToolStatus[]>>;
}): Promise<{ data: any; clientCaptureRequired?: ClientCaptureRequest }> {
  // System prompt is applied server-side (POST /api/digisakgpt/agent).
  const isInstant = options.thinkingDisabled === true;
  const body: Record<string, unknown> = {
    model: 'kimi-k2.6',
    messages: options.messages,
    temperature: isInstant ? 0.6 : 1.0,
    top_p: 0.95,
    max_tokens: isInstant ? 2048 : 8192,
  };
  if (isInstant) body.thinking_disabled = true;
  if (options.conversationId) body.conversation_id = options.conversationId;
  if (options.farmId) body.farm_id = options.farmId;
  if (options.agentResume) {
    body.agent_resume = true;
    body.resume_messages = options.resumeMessages ?? [];
  }

  const resp = await fetch(
    DIGISAKA_API_ORIGIN + '/api/digisakgpt/agent',
    digisakaAuthRequestInit({
      method: 'POST',
      headers: { ...gptInferenceJsonHeaders() },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(300000),
    })
  );

  if (!resp.ok) {
    const errText = await resp.text();
    throw new Error(errText || `HTTP ${resp.status}`);
  }

  if (!resp.body) {
    throw new Error(`${PANDOY_AI_DISPLAY_NAME} returned an empty response body.`);
  }

  // Successful agent responses stream SSE; do not rely on Content-Type (proxies may omit or rewrite it).
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let answerData: any = null;
  let clientCaptureRequired: ClientCaptureRequest | undefined;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const { rest, frames } = consumeCompleteSseFrames(buffer);
    buffer = rest;

    for (const frame of frames) {
      const eventType = frame.event.trim();
      const rawData = frame.data.trim();
      if (!rawData) continue;

      try {
        const parsed = JSON.parse(rawData);

        if (eventType === 'tool_start') {
          // A tool round after optimistic answer_delta means discard the partial draft.
          options.onStreamReset?.();
          const tool = typeof parsed.tool === 'string' ? parsed.tool.trim() : '';
          const label = farmerToolLabel(
            tool,
            typeof parsed.label === 'string' ? parsed.label : ''
          );
          const toolKey = tool || `tool_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
          options.setToolStatuses?.((prev) => [...prev, { tool: toolKey, label, done: false }]);
        } else if (eventType === 'tool_done') {
          const tool = typeof parsed.tool === 'string' ? parsed.tool.trim() : '';
          options.setToolStatuses?.((prev) => {
            const idx = prev.findIndex((s) => s.tool === tool && !s.done);
            if (idx < 0) return prev;
            return prev.map((s, i) => (i === idx ? { ...s, done: true } : s));
          });
        } else if (eventType === 'compute') {
          const ms = typeof parsed.ms === 'number' ? parsed.ms : parseInt(String(parsed.ms ?? ''), 10);
          if (Number.isFinite(ms) && ms > 0) {
            applyComputeMsFromHeaderValue(String(ms));
          }
        } else if (eventType === 'client_capture_required') {
          const tool = typeof parsed.tool === 'string' ? parsed.tool.trim() : '';
          const toolCallId = typeof parsed.tool_call_id === 'string' ? parsed.tool_call_id.trim() : '';
          const assistantMessage =
            parsed.assistant_message && typeof parsed.assistant_message === 'object'
              ? parsed.assistant_message
              : {};
          if (tool && toolCallId) {
            clientCaptureRequired = { tool, toolCallId, assistantMessage };
          }
        } else if (eventType === 'paused') {
          if (clientCaptureRequired) {
            return { data: answerData, clientCaptureRequired };
          }
        } else if (eventType === 'answer_delta') {
          const delta =
            typeof parsed.delta === 'string'
              ? parsed.delta
              : typeof parsed.content === 'string'
                ? parsed.content
                : '';
          if (delta) options.onAnswerDelta?.(delta);
        } else if (eventType === 'answer') {
          answerData = parsed;
          options.onAnswer?.(parsed);
        } else if (eventType === 'error') {
          throw new Error(
            typeof parsed.detail === 'string'
              ? parsed.detail
              : JSON.stringify(parsed.detail)
          );
        }
      } catch (e: any) {
        if (eventType === 'error' || eventType === 'answer') throw e;
      }
    }
  }

  if (clientCaptureRequired) return { data: answerData, clientCaptureRequired };
  if (answerData) return { data: answerData };
  throw new Error(`No answer received from ${PANDOY_AI_DISPLAY_NAME}.`);
}

function formatChatListTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function GptChatSidebar({
  isDark,
  conversations,
  activeId,
  searchQuery,
  onSearchChange,
  onSelect,
  onNewChat,
  onClose,
  disabled,
  renamingId,
  renameDraft,
  onRenameDraftChange,
  onStartRename,
  onCancelRename,
  onSubmitRename,
  deleteConfirmId,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
}: {
  isDark: boolean;
  conversations: GptConversationSummary[];
  activeId: string | null;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  onClose?: () => void;
  disabled?: boolean;
  renamingId: string | null;
  renameDraft: string;
  onRenameDraftChange: (v: string) => void;
  onStartRename: (id: string, currentTitle: string) => void;
  onCancelRename: () => void;
  onSubmitRename: (id: string) => void;
  deleteConfirmId: string | null;
  onRequestDelete: (id: string) => void;
  onCancelDelete: () => void;
  onConfirmDelete: (id: string) => void;
}) {
  const sb = getSidebarTheme(isDark);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => c.title.toLowerCase().includes(q));
  }, [conversations, searchQuery]);

  const searchCls = isDark
    ? 'w-full rounded-[10px] pl-8 pr-8 py-2 text-xs bg-[#1c2432] border border-[#2d3748] text-[#e6edf3] placeholder:text-[#6e7681] focus:outline-none focus:ring-1 focus:ring-[#34d399]/40 focus:border-[#34d399]'
    : 'w-full rounded-[10px] pl-8 pr-8 py-2 text-xs bg-white border border-[#e5e7eb] text-[#111827] placeholder:text-[#6b7280] focus:outline-none focus:ring-1 focus:ring-[#008540CC] focus:border-[#008540]';
  const rowBase = isDark
    ? 'hover:bg-white/[0.06] text-[#c9d1d9]'
    : 'hover:bg-[#f3f4f6] text-[#374151]';
  const rowActive = isDark
    ? 'bg-[rgba(52,211,153,.16)] text-[#e6edf3] border border-[#34d399]/35'
    : 'bg-[rgba(0,133,64,.12)] text-[#111827] border border-[#00854080]';

  return (
    <div
      className={`flex flex-col h-full w-[230px] min-w-[230px] ${isDark ? 'text-[#e6edf3]' : 'text-[#111827]'}`}
    >
      <div
        className={`shrink-0 p-2 space-y-2 border-b ${sb.headerBorder}`}
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={disabled}
            onClick={onNewChat}
            className={`flex-1 flex items-center gap-2 rounded-[10px] border px-2.5 py-2 text-xs font-medium transition-colors disabled:opacity-50 ${sb.fullBtn}`}
          >
            <i className="ri-add-line text-base shrink-0" aria-hidden />
            <span>New chat</span>
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className={`p-2 rounded-lg shrink-0 ${
                isDark
                  ? 'text-[#8b949e] hover:bg-white/[0.06] hover:text-[#e6edf3]'
                  : 'text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#008540]'
              }`}
              aria-label="Close chat list"
            >
              <i className="ri-close-line text-lg" />
            </button>
          )}
        </div>
        <div className="relative">
          <i
            className={`ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-sm pointer-events-none ${
              isDark ? 'text-[#6e7681]' : 'text-[#6b7280]'
            }`}
            aria-hidden
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search chats"
            disabled={disabled}
            aria-label="Search chats"
            className={`w-full disabled:opacity-50 ${searchCls}`}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className={`absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded ${
                isDark ? 'text-[#8b949e] hover:text-[#e6edf3]' : 'text-[#6b7280] hover:text-[#111827]'
              }`}
              aria-label="Clear search"
            >
              <i className="ri-close-line text-xs" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-0.5">
        {filtered.length === 0 ? (
          <p className={`px-2 py-6 text-center text-xs ${sb.emptyText}`}>
            {searchQuery.trim() ? 'No chats match your search' : 'No saved chats yet'}
          </p>
        ) : (
          filtered.map((c) => {
            const isActive = c.id === activeId;
            const isRenaming = renamingId === c.id;
            const isDeleting = deleteConfirmId === c.id;

            return (
              <div
                key={c.id}
                className={`group rounded-lg px-2 py-1.5 transition-colors ${
                  isActive ? rowActive : rowBase
                }`}
              >
                {isDeleting ? (
                  <div className="space-y-2">
                    <p className={`text-[11px] ${isDark ? 'text-[#c9d1d9]' : 'text-[#374151]'}`}>
                      Delete this chat?
                    </p>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onConfirmDelete(c.id)}
                        className="flex-1 px-2 py-1 rounded-md text-[10px] font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={onCancelDelete}
                        className={`flex-1 px-2 py-1 rounded-md text-[10px] border disabled:opacity-50 ${sb.secondaryBtn}`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : isRenaming ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={renameDraft}
                      onChange={(e) => onRenameDraftChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onSubmitRename(c.id);
                        if (e.key === 'Escape') onCancelRename();
                      }}
                      maxLength={120}
                      autoFocus
                      className={`flex-1 min-w-0 rounded-md border px-2 py-1 text-[11px] focus:outline-none focus:ring-1 ${
                        isDark
                          ? `${sb.input} py-1`
                          : 'border-white/40 bg-white text-gray-900 focus:ring-white/60'
                      }`}
                      aria-label="Rename chat"
                    />
                    <button
                      type="button"
                      disabled={disabled || !renameDraft.trim()}
                      onClick={() => onSubmitRename(c.id)}
                      className={`p-1 rounded disabled:opacity-40 ${
                        isDark ? 'text-[#34d399] hover:text-[#6ee7b7]' : 'text-[#008540] hover:opacity-80'
                      }`}
                      aria-label="Save name"
                    >
                      <i className="ri-check-line text-sm" />
                    </button>
                    <button
                      type="button"
                      onClick={onCancelRename}
                      className={`p-1 rounded ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}
                      aria-label="Cancel rename"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-start gap-1 min-w-0">
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => onSelect(c.id)}
                      className="flex-1 min-w-0 text-left disabled:opacity-50"
                    >
                      <span
                        className={`flex items-center gap-1 text-xs font-medium leading-snug min-w-0 ${
                          isActive && isDark ? 'text-white' : ''
                        }`}
                      >
                        {c.pinned && <i className="ri-pushpin-fill text-[11px] shrink-0" aria-hidden />}
                        {c.is_favourite && (
                          <i className="ri-star-fill text-[11px] text-amber-500 shrink-0" aria-hidden />
                        )}
                        <span className="truncate">{c.title}</span>
                      </span>
                      {c.updated_at && (
                        <span
                          className={`block text-[10px] truncate mt-0.5 ${
                            isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'
                          }`}
                        >
                          {formatChatListTime(c.updated_at)}
                        </span>
                      )}
                    </button>
                    <div className="flex shrink-0 flex-col gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100">
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onStartRename(c.id, c.title)}
                        className={`p-1 rounded transition-colors ${
                          isDark
                            ? 'text-[#8b949e] hover:bg-white/[0.06] hover:text-[#e6edf3]'
                            : 'text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#008540]'
                        }`}
                        aria-label={`Rename ${c.title}`}
                      >
                        <i className="ri-edit-line text-xs" />
                      </button>
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onRequestDelete(c.id)}
                        className={`p-1 rounded transition-colors ${
                          isDark
                            ? 'text-red-400 hover:bg-white/[0.06]'
                            : 'text-red-600 hover:bg-[#f3f4f6]'
                        }`}
                        aria-label={`Delete ${c.title}`}
                      >
                        <i className="ri-delete-bin-line text-xs" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function GptAnalyseOfferCard({
  onAccept,
  onDismiss,
  isLoading,
  isAutoAnalysing,
  disabled = false,
  className = '',
  isDark = false,
  title = 'Analyse with Pandoy AI?',
  description = 'Pandoy AI will reason about your map view and current results to answer your questions. You can also run analyses directly from the chat.',
}: {
  onAccept: () => void;
  onDismiss: () => void;
  isLoading: boolean;
  isAutoAnalysing: boolean;
  disabled?: boolean;
  className?: string;
  isDark?: boolean;
  title?: string;
  description?: string;
}) {
  return (
    <div
      className={`rounded-2xl border px-3 sm:px-4 py-2.5 sm:py-3 text-xs flex items-start gap-2 sm:gap-2.5 ${
        isDark
          ? 'border-[#2d3748] bg-[#1c2432] text-[#c9d1d9]'
          : 'border-[#e5e7eb] bg-white text-[#374151]'
      } ${className}`}
      style={{ animation: 'gpt-pill-in 350ms ease-out both' }}
    >
      <div
        className={`mt-0.5 shrink-0 flex h-7 w-7 items-center justify-center rounded-full ${
          isDark ? 'bg-[rgba(52,211,153,.16)] text-[#34d399]' : 'bg-[#00854014] text-[#008540]'
        }`}
      >
        <i className="ri-brain-line text-base" aria-hidden />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold mb-1 text-[13px] leading-snug ${isDark ? 'text-[#e6edf3]' : 'text-[#111827]'}`}>
          {title}
        </div>
        <div className={`text-[11.5px] mb-2 leading-snug ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>
          {description}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={onAccept}
            disabled={disabled || isLoading || isAutoAnalysing}
            className={`px-2.5 py-1 rounded-full text-white text-[11px] font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 ${
              isDark ? 'bg-[#008540] hover:bg-[#097a34]' : ''
            }`}
            style={isDark ? undefined : { backgroundColor: BRAND_GREEN }}
          >
            {isLoading || isAutoAnalysing ? (
              <>
                <span className="w-2.5 h-2.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Looking this over…
              </>
            ) : (
              <>
                <i className="ri-check-double-line text-[13px]" />
                Yes, analyse
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className={`px-2.5 py-1 rounded-full border text-[11px] ${
              isDark
                ? 'border-[#2d3748] text-[#c9d1d9] hover:bg-white/[0.06]'
                : 'border-[#e5e7eb] text-[#6b7280] hover:bg-[#f3f4f6]'
            }`}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

/** Pandoy logo with expanding ring animation while waiting for a response. */
function GptPandoyWaitingIndicator({
  isDark,
  thinkingMode,
}: {
  isDark: boolean;
  thinkingMode: boolean;
}) {
  const ringClass = thinkingMode
    ? isDark
      ? 'border-amber-500/50'
      : 'border-amber-400/60'
    : isDark
      ? 'border-gray-500/60'
      : 'border-[#008540]/50';

  return (
    <div className="flex justify-start">
      <div
        className={`rounded-[4px_14px_14px_14px] shadow-sm flex items-center gap-2.5 ${
          thinkingMode ? 'border px-4 py-3' : 'p-1'
        } ${
          isDark
            ? thinkingMode
              ? 'bg-[#1c2432] border-[#2d3748]'
              : ''
            : thinkingMode
              ? 'bg-white border-[#e5e7eb]'
              : ''
        }`}
      >
        <div className="relative w-9 h-9 flex items-center justify-center shrink-0" aria-hidden>
          <span
            className={`absolute w-full h-full rounded-full border-2 pointer-events-none ${ringClass}`}
            style={{ animation: 'bubble-wave 2s ease-out infinite' }}
          />
          <span
            className={`absolute w-full h-full rounded-full border-2 pointer-events-none ${ringClass}`}
            style={{ animation: 'bubble-wave 2s ease-out infinite', animationDelay: '1s' }}
          />
          <img src={digisakaGptLogo} alt="" className="relative z-10 w-7 h-7 object-contain" />
        </div>
        {thinkingMode ? (
          <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Thinking…
          </span>
        ) : (
          <span className="sr-only">Waiting for response</span>
        )}
      </div>
    </div>
  );
}

function GptFieldQuickActionsCard({
  fieldContext,
  starters,
  onStarter,
  isLoading,
  isDark,
}: {
  fieldContext: GptFieldContext;
  starters: PandoyStarter[];
  onStarter: (starter: PandoyStarter) => void;
  isLoading: boolean;
  isDark: boolean;
}) {
  const fieldLabel = fieldContext.farmName?.trim() || `Farm ${fieldContext.farmId}`;
  const manyBlocks = (fieldContext.blockCount ?? 1) > 1;

  return (
    <GptChatPillsSection
      title={manyBlocks ? `Compare blocks on ${fieldLabel}` : `Ask about ${fieldLabel}`}
      iconClass="ri-map-pin-line"
      isDark={isDark}
    >
      {starters.map((starter) => (
        <button
          key={starter.id}
          type="button"
          disabled={isLoading}
          title="Click to ask this question"
          onClick={() => onStarter(starter)}
          className={gptPillButtonClass(isDark, isLoading)}
        >
          {starter.label}
        </button>
      ))}
    </GptChatPillsSection>
  );
}

function GptDashboardAnalyzeCard({
  isDark,
  disabled,
  starters,
  onStarter,
}: {
  isDark: boolean;
  disabled?: boolean;
  starters?: PandoyStarter[];
  onStarter?: (starter: PandoyStarter) => void;
}) {
  if (!starters || starters.length === 0 || !onStarter) return null;
  return (
    <GptChatPillsSection title="Suggested for you" iconClass="ri-sparkling-line" isDark={isDark}>
      {starters.map((starter) => (
        <button
          key={starter.id}
          type="button"
          disabled={disabled}
          title="Click to ask this question"
          onClick={() => onStarter(starter)}
          className={gptPillButtonClass(isDark, disabled)}
        >
          {starter.label}
        </button>
      ))}
    </GptChatPillsSection>
  );
}

/**
 * Map a short ISO 639-1 language code to a BCP-47 tag that
 * the Web Speech API handles well. Falls back to the raw code
 * when there is no known mapping.
 */
function voiceLangFromIso(iso: string): string {
  const map: Record<string, string> = {
    en: 'en-US',
    fil: 'fil-PH',
    tl: 'fil-PH',
    es: 'es-ES',
    pt: 'pt-BR',
    fr: 'fr-FR',
    de: 'de-DE',
    it: 'it-IT',
    nl: 'nl-NL',
    ja: 'ja-JP',
    ko: 'ko-KR',
    zh: 'zh-CN',
    ar: 'ar-SA',
    ru: 'ru-RU',
    hi: 'hi-IN',
    vi: 'vi-VN',
    th: 'th-TH',
    id: 'id-ID',
    ms: 'ms-MY',
    tr: 'tr-TR',
    pl: 'pl-PL',
    sv: 'sv-SE',
    da: 'da-DK',
    fi: 'fi-FI',
    nb: 'nb-NO',
    cs: 'cs-CZ',
    hu: 'hu-HU',
    ro: 'ro-RO',
    el: 'el-GR',
    he: 'he-IL',
  };
  return map[iso] || iso;
}

export default function DigisakaLLMChat({
  autoRequest,
  screenshotTargetRef,
  dashboardScreenshotRef,
  onAutoRequestConsumed,
  fieldContext = null,
  onChatFullscreenChange,
  onPandoyPageChange,
  hideCornerLauncher = false,
}: DigisakaLLMChatProps) {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const pd = getPandoyTheme(isDark);
  const roleStarters = useMemo(
    () => pandoyRoleStarters(user?.role, user?.sub_role, user?.monitoring_area),
    [user?.role, user?.sub_role, user?.monitoring_area]
  );
  const fieldStarters = useMemo(
    () =>
      pandoyFieldStarters(user?.role, user?.sub_role, fieldContext).slice(0, 4),
    [user?.role, user?.sub_role, fieldContext]
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [attachedImage, setAttachedImage] = useState<string | null>(null);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isChatFullscreen, setIsChatFullscreen] = useState(false);
  /** `page` fills the area beside the side menu. `popup` is the corner card. */
  const [chatLayout, setChatLayout] = useState<PandoyOpenMode>('popup');
  const chatLayoutRef = useRef(chatLayout);
  chatLayoutRef.current = chatLayout;
  const hideCornerLauncherRef = useRef(hideCornerLauncher);
  hideCornerLauncherRef.current = hideCornerLauncher;
  const [isAutoAnalysing, setIsAutoAnalysing] = useState(false);
  const [isCapturingDashboard, setIsCapturingDashboard] = useState(false);
  const [showAutoSuccess, setShowAutoSuccess] = useState(false);
  const [toolStatuses, setToolStatuses] = useState<ToolStatus[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isImageDragOver, setIsImageDragOver] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [memories, setMemories] = useState<GptMemoryItem[]>([]);
  const [memoryBusy, setMemoryBusy] = useState(false);
  const [conversationList, setConversationList] = useState<GptConversationSummary[]>([]);
  const [conversationHydrating, setConversationHydrating] = useState(false);
  const [chatSidebarOpen, setChatSidebarOpen] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [renamingConversationId, setRenamingConversationId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [chatActionBusy, setChatActionBusy] = useState(false);
  const [isVoiceLoop, setIsVoiceLoop] = useState(false);
  const isVoiceLoopRef = useRef(false);
  const [thinkingMode, setThinkingMode] = useState(false);
  const thinkingModeRef = useRef(false);
  const [assistantTypingDoneId, setAssistantTypingDoneId] = useState<string | null>(null);
  const [autoPopCountdownSec, setAutoPopCountdownSec] = useState<number | null>(null);
  const userDismissedAutoExpandRef = useRef(isPandoyAutoExpandDismissed());
  const autoExpandTimerRef = useRef<number | null>(null);
  const autoPopCountdownIntervalRef = useRef<number | null>(null);
  const isOpenRef = useRef(isOpen);
  const isMinimizedRef = useRef(isMinimized);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    isMinimizedRef.current = isMinimized;
  }, [isMinimized]);

  const clearAutoPopCountdown = useCallback(() => {
    if (autoPopCountdownIntervalRef.current != null) {
      window.clearInterval(autoPopCountdownIntervalRef.current);
      autoPopCountdownIntervalRef.current = null;
    }
    setAutoPopCountdownSec(null);
  }, []);

  const cancelAutoExpandTimer = useCallback(() => {
    if (autoExpandTimerRef.current != null) {
      window.clearTimeout(autoExpandTimerRef.current);
      autoExpandTimerRef.current = null;
    }
    clearAutoPopCountdown();
  }, [clearAutoPopCountdown]);

  const dismissAutoExpand = useCallback(() => {
    userDismissedAutoExpandRef.current = true;
    markPandoyAutoExpandDismissed();
    cancelAutoExpandTimer();
  }, [cancelAutoExpandTimer]);

  const openChatPanel = useCallback((layout: PandoyOpenMode = 'popup') => {
    setChatLayout(layout);
    if (layout === 'page') setIsChatFullscreen(false);
    setIsMinimized(false);
    setIsOpen(true);
    setIsAnimating(true);
    window.setTimeout(() => setIsAnimating(false), 800);
  }, []);

  // The side menu opens Pandoy as a page. The corner button keeps the popup.
  useEffect(() => {
    const onOpenRequest = (event: Event) => {
      const mode = (event as CustomEvent<{ mode?: PandoyOpenMode }>).detail?.mode ?? 'popup';
      cancelAutoExpandTimer();
      openChatPanel(mode === 'page' ? 'page' : 'popup');
    };
    const onCloseRequest = () => {
      if (chatLayoutRef.current !== 'page') return;
      setChatLayout('popup');
      setIsOpen(false);
    };
    window.addEventListener(OPEN_PANDOY_EVENT, onOpenRequest);
    window.addEventListener(CLOSE_PANDOY_EVENT, onCloseRequest);
    return () => {
      window.removeEventListener(OPEN_PANDOY_EVENT, onOpenRequest);
      window.removeEventListener(CLOSE_PANDOY_EVENT, onCloseRequest);
    };
  }, [cancelAutoExpandTimer, openChatPanel]);

  const scheduleDelayedOpen = useCallback(
    (delayMs: number, opts?: { respectDismiss?: boolean }) => {
      cancelAutoExpandTimer();
      const respectDismiss = opts?.respectDismiss ?? false;
      if (respectDismiss && userDismissedAutoExpandRef.current) return;
      if (hideCornerLauncherRef.current) return;
      if (isOpenRef.current || isMinimizedRef.current) return;

      const endMs = Date.now() + delayMs;
      setAutoPopCountdownSec(Math.ceil(delayMs / 1000));
      autoPopCountdownIntervalRef.current = window.setInterval(() => {
        const remaining = Math.max(0, Math.ceil((endMs - Date.now()) / 1000));
        setAutoPopCountdownSec(remaining > 0 ? remaining : null);
      }, 250);

      autoExpandTimerRef.current = window.setTimeout(() => {
        autoExpandTimerRef.current = null;
        clearAutoPopCountdown();
        if (respectDismiss && userDismissedAutoExpandRef.current) return;
        if (hideCornerLauncherRef.current) return;
        if (isOpenRef.current || isMinimizedRef.current) return;
        openChatPanel();
      }, delayMs);
    },
    [cancelAutoExpandTimer, clearAutoPopCountdown, openChatPanel]
  );

  const handledAutoRequestRef = useRef<AutoRequest | null>(null);
  const onAutoRequestConsumedRef = useRef(onAutoRequestConsumed);
  useEffect(() => {
    onAutoRequestConsumedRef.current = onAutoRequestConsumed;
  }, [onAutoRequestConsumed]);

  // Sync refs with state
  useEffect(() => {
    isVoiceLoopRef.current = isVoiceLoop;
  }, [isVoiceLoop]);
  useEffect(() => {
    thinkingModeRef.current = thinkingMode;
  }, [thinkingMode]);
  // Derive the voice language from the user's browser/system language (navigator.language)
  // so speech recognition works in whatever language the user actually speaks.
  // Falls back to the i18n app language if navigator is unavailable.
  const [voiceLang, setVoiceLang] = useState(() => {
    const navLang = navigator?.language || '';
    const raw = navLang.split('-')[0] || i18n.language?.split('-')[0] || 'en';
    return voiceLangFromIso(raw);
  });

  // Stay in sync if the user changes language in-app (e.g. toggles locale)
  useEffect(() => {
    const handler = (lng: string) => {
      const raw = lng?.split('-')[0] || 'en';
      setVoiceLang(voiceLangFromIso(raw));
    };
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, []);

  // Voice input (speech-to-text)
  const sendAfterVoiceRef = useRef(false);
  const sendAgentTurnRef = useRef<(opts: { displayText: string; agentContent: string | unknown[]; imageUrl?: string | null; farmId?: string | null }) => void>();
  const pendingVoiceLoopNextRef = useRef(false);

  const {
    start: startListening,
    stop: stopListening,
    transcript: voiceTranscript,
    isListening,
    isSupported: sttSupported,
  } = useSpeechToText({
    language: voiceLang,
    interimResults: true,
    silenceTimeoutMs: 1500,
    onSpeechEnd: (fullTranscript) => {
      const trimmed = fullTranscript.trim();
      if (trimmed && sendAfterVoiceRef.current) {
        setInputText(trimmed);
        setTimeout(() => {
          sendAfterVoiceRef.current = false;
          const send = sendAgentTurnRef.current;
          send({
            displayText: trimmed,
            agentContent: trimmed,
            farmId: fieldContext?.farmId ?? null,
          });
          setInputText('');
          // If voice loop mode, prepare to re-listen after the response is spoken
          if (isVoiceLoopRef.current) {
            pendingVoiceLoopNextRef.current = true;
          }
        }, 50);
      } else {
        sendAfterVoiceRef.current = false;
      }
    },
  });

  // Sync live voice transcript into the input text area in real time
  useEffect(() => {
    if (isListening && voiceTranscript) {
      setInputText(voiceTranscript);
    }
  }, [isListening, voiceTranscript]);

  // Voice output (text-to-speech)
  const {
    speak: speakText,
    stop: stopSpeaking,
    isSpeaking,
    isSupported: ttsSupported,
  } = useTextToSpeech({
    lang: voiceLang,
    rate: 1,
    pitch: 1,
  });

  const [lastSpokenMessageId, setLastSpokenMessageId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const conversationPrimedRef = useRef(false);
  const prevFieldIdRef = useRef<string | null | undefined>(undefined);

  const scrollChatToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const lastAssistantMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (!messages[i].isUser) return messages[i].id;
    }
    return null;
  }, [messages]);

  /** Clear the active chat without creating a server conversation (saved only after the first message). */
  const startFreshChat = useCallback(() => {
    setRenamingConversationId(null);
    setDeleteConfirmId(null);
    setRenameDraft('');
    setInputText('');
    setAttachedImage(null);
    if (textareaRef.current) textareaRef.current.style.height = '40px';
    sessionStorage.removeItem(DIGISAKA_GPT_CONVERSATION_STORAGE_KEY);
    setConversationId(null);
    setMessages([]);
    setAssistantTypingDoneId(null);
  }, []);

  /** Create a conversation on the server when the user sends their first message. */
  const ensureConversationId = useCallback(async (): Promise<string | null> => {
    if (conversationId) return conversationId;
    try {
      const { id } = await createDigisakaGptConversation();
      sessionStorage.setItem(DIGISAKA_GPT_CONVERSATION_STORAGE_KEY, id);
      setConversationId(id);
      return id;
    } catch {
      return null;
    }
  }, [conversationId]);

  // Always start with a blank chat after a full page refresh.
  useEffect(() => {
    sessionStorage.removeItem(DIGISAKA_GPT_CONVERSATION_STORAGE_KEY);
  }, []);

  const refreshConversationList = useCallback(async () => {
    try {
      const items = await listDigisakaGptConversations();
      setConversationList(items);
    } catch {
      setConversationList([]);
    }
  }, []);

  const handleRenameConversation = useCallback(
    async (id: string, title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return;
      setChatActionBusy(true);
      try {
        const updated = await renameDigisakaGptConversation(id, trimmed);
        setConversationList((prev) =>
          prev.map((c) =>
            c.id === id
              ? {
                  ...c,
                  title: updated.title,
                  pinned: updated.pinned,
                  is_favourite: updated.is_favourite,
                  updated_at: updated.updated_at,
                }
              : c
          )
        );
        setRenamingConversationId(null);
        setRenameDraft('');
      } catch (e) {
        console.error('[Digisaka GPT] Rename failed', e);
      } finally {
        setChatActionBusy(false);
      }
    },
    []
  );

  const handleConversationFlag = useCallback(
    async (flag: 'pinned' | 'is_favourite') => {
      if (!conversationId || chatActionBusy) return;
      const current = conversationList.find((c) => c.id === conversationId);
      const next = flag === 'pinned' ? !current?.pinned : !current?.is_favourite;
      setChatActionBusy(true);
      try {
        const updated = await updateDigisakaGptConversationFlags(conversationId, { [flag]: next });
        setConversationList((prev) => {
          const mapped = prev.map((c) =>
            c.id === conversationId
              ? { ...c, pinned: updated.pinned, is_favourite: updated.is_favourite, updated_at: updated.updated_at }
              : c
          );
          return [...mapped].sort((a, b) => {
            if (Boolean(a.pinned) !== Boolean(b.pinned)) return a.pinned ? -1 : 1;
            if (Boolean(a.is_favourite) !== Boolean(b.is_favourite)) return a.is_favourite ? -1 : 1;
            return String(b.updated_at || '').localeCompare(String(a.updated_at || ''));
          });
        });
      } catch (e) {
        console.error('[Digisaka GPT] Could not update chat', e);
      } finally {
        setChatActionBusy(false);
      }
    },
    [chatActionBusy, conversationId, conversationList]
  );

  const refreshMemories = useCallback(async () => {
    setMemoryBusy(true);
    try {
      setMemories(await listDigisakaGptMemories());
    } catch (e) {
      console.error('[Digisaka GPT] Could not load memory', e);
      setMemories([]);
    } finally {
      setMemoryBusy(false);
    }
  }, []);

  const handleDeleteMemory = useCallback(async (memoryId: string) => {
    setMemoryBusy(true);
    try {
      await deleteDigisakaGptMemory(memoryId);
      setMemories((prev) => prev.filter((item) => item.id !== memoryId));
    } catch (e) {
      console.error('[Digisaka GPT] Could not delete memory', e);
    } finally {
      setMemoryBusy(false);
    }
  }, []);

  /** Load chat history sidebar once the panel opens; do not restore or create a conversation yet. */
  useEffect(() => {
    if (!isOpen) return;
    if (conversationPrimedRef.current) return;

    let cancelled = false;

    (async () => {
      try {
        await refreshConversationList();
      } finally {
        if (!cancelled) conversationPrimedRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, refreshConversationList]);

  /** Selecting a different field starts a new chat so quick actions match the active field. */
  useEffect(() => {
    const nextFieldId = fieldContext?.farmId ?? null;
    if (prevFieldIdRef.current === undefined) {
      prevFieldIdRef.current = nextFieldId;
      return;
    }
    if (prevFieldIdRef.current !== nextFieldId) {
      prevFieldIdRef.current = nextFieldId;
      startFreshChat();
    }
  }, [fieldContext?.farmId, startFreshChat]);

  useEffect(() => {
    if (!isOpen || !conversationId) return;
    refreshConversationList();
  }, [isOpen, conversationId, refreshConversationList]);

  useEffect(() => {
    if (chatLayout === 'page') {
      setChatSidebarOpen(true);
      return;
    }
    setChatSidebarOpen(isChatFullscreen);
  }, [isChatFullscreen, chatLayout]);

  useEffect(() => {
    if (!isOpen && isChatFullscreen) {
      setIsChatFullscreen(false);
    }
    if (!isOpen && chatLayout === 'page') {
      setChatLayout('popup');
    }
  }, [isOpen, isChatFullscreen, chatLayout]);

  useEffect(() => {
    onChatFullscreenChange?.(isOpen && isChatFullscreen);
  }, [isOpen, isChatFullscreen, onChatFullscreenChange]);

  useEffect(() => {
    onPandoyPageChange?.(isOpen && chatLayout === 'page');
  }, [isOpen, chatLayout, onPandoyPageChange]);

  useEffect(() => {
    if (!hideCornerLauncher) return;
    cancelAutoExpandTimer();
    if (chatLayoutRef.current === 'popup') {
      setIsOpen(false);
      setIsChatFullscreen(false);
    }
  }, [hideCornerLauncher, cancelAutoExpandTimer]);

  const switchConversation = useCallback(
    async (nextId: string) => {
      if (!nextId || nextId === conversationId) return;
      setRenamingConversationId(null);
      setDeleteConfirmId(null);
      setConversationHydrating(true);
      try {
        const rows = await fetchDigisakaGptMessages(nextId);
        sessionStorage.setItem(DIGISAKA_GPT_CONVERSATION_STORAGE_KEY, nextId);
        setConversationId(nextId);
        const mapped = rows.map(apiStoredRowToMessage);
        setMessages(mapped);
        setAssistantTypingDoneId(lastAssistantIdFromMessages(mapped));
        await refreshConversationList();
      } catch (e) {
        console.error('[Digisaka GPT] Failed to load conversation', e);
      } finally {
        setConversationHydrating(false);
      }
    },
    [conversationId, refreshConversationList]
  );

  const handleDeleteConversation = useCallback(
    async (id: string) => {
      setChatActionBusy(true);
      try {
        await deleteDigisakaGptConversation(id);
        setDeleteConfirmId(null);
        setRenamingConversationId(null);

        if (conversationId === id) {
          const items = await listDigisakaGptConversations();
          if (items.length > 0) {
            setConversationHydrating(true);
            const rows = await fetchDigisakaGptMessages(items[0].id);
            sessionStorage.setItem(DIGISAKA_GPT_CONVERSATION_STORAGE_KEY, items[0].id);
            setConversationId(items[0].id);
            const mapped = rows.map(apiStoredRowToMessage);
            setMessages(mapped);
            setAssistantTypingDoneId(lastAssistantIdFromMessages(mapped));
            setConversationList(items);
            setConversationHydrating(false);
          } else {
            startFreshChat();
            await refreshConversationList();
          }
        } else {
          await refreshConversationList();
        }
      } catch (e) {
        console.error('[Digisaka GPT] Delete failed', e);
      } finally {
        setChatActionBusy(false);
      }
    },
    [conversationId, refreshConversationList, startFreshChat]
  );

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Sync live voice transcript into the text input area
  useEffect(() => {
    if (isListening && voiceTranscript) {
      setInputText(voiceTranscript);
    }
  }, [isListening, voiceTranscript]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '40px';
      const scrollHeight = textareaRef.current.scrollHeight;
      if (scrollHeight > 40) {
        textareaRef.current.style.height = `${Math.min(scrollHeight, 120)}px`;
      }
    }
  }, [inputText]);

  const handleResetChat = () => {
    startFreshChat();
  };

  const handleToggleChat = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsAnimating(false);
    }, 600);

    if (isOpen) {
      dismissAutoExpand();
      setChatLayout('popup');
      setIsOpen(false);
      return;
    }

    cancelAutoExpandTimer();
    setChatLayout('popup');
    setIsOpen(true);
  };

  const handleMinimize = (e?: React.MouseEvent) => {
    e?.stopPropagation?.(); // Prevent triggering the logo click when called from button
    setIsMinimized(true);
  };

  const handleRestore = () => {
    setIsMinimized(false);
  };

  const hasUnseenAnalyseOffer = Boolean(
    autoRequest && !String(autoRequest.pasteText || '').trim() && !isOpen
  );

  const processImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        setAttachedImage(result);
      } else {
        setAttachedImage(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      setAttachedImage(null);
      return;
    }
    processImageFile(file);
  };

  const handleImagePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = event.clipboardData?.items;
    if (!items?.length) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) processImageFile(file);
        return;
      }
    }
  };

  const handleImageDropZoneDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.types.includes('Files')) {
      setIsImageDragOver(true);
    }
  };

  const handleImageDropZoneDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsImageDragOver(false);
    }
  };

  const handleImageDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsImageDragOver(false);
    const files = event.dataTransfer.files;
    if (!files?.length) return;
    const imageFile = Array.from(files).find((f) => f.type.startsWith('image/'));
    if (imageFile) processImageFile(imageFile);
  };

  const sendAgentTurn = useCallback(
    async (options: {
      displayText: string;
      agentContent: string | unknown[];
      imageUrl?: string | null;
      farmId?: string | null;
    }) => {
      const imageUrl = options.imageUrl ?? null;
      const agentContent = options.agentContent;

      const userMessage: Message = {
        id: Date.now().toString(),
        text: options.displayText,
        isUser: true,
        timestamp: new Date(),
        imageUrl: imageUrl || undefined,
      };
      const priorMessages = messages;
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      try {
        const activeConversationId = await ensureConversationId();
        let requestMessages: { role: 'user' | 'assistant'; content: any }[];

        if (activeConversationId) {
          requestMessages = [{ role: 'user', content: agentContent }];
        } else {
          const historyMessages = priorMessages
            .map((m) => ({
              role: m.isUser ? ('user' as const) : ('assistant' as const),
              content: m.text,
            }))
            .filter((m) => !(m.role === 'user' && (!m.content || String(m.content).trim() === '')));
          requestMessages = [...historyMessages, { role: 'user', content: agentContent }];
        }

        setToolStatuses([]);
        let answerHandled = false;
        let streamingMessageId: string | null = null;

        const handleAnswerDelta = (delta: string) => {
          if (!delta || answerHandled) return;
          if (!streamingMessageId) {
            streamingMessageId = `${Date.now() + 1}`;
            setAssistantTypingDoneId(null);
            setMessages((prev) => [
              ...prev,
              {
                id: streamingMessageId!,
                text: delta,
                isUser: false,
                timestamp: new Date(),
                revealTyping: false,
                isStreaming: true,
              },
            ]);
            setIsLoading(false);
            return;
          }
          const targetId = streamingMessageId;
          setMessages((prev) =>
            prev.map((m) => (m.id === targetId ? { ...m, text: `${m.text}${delta}` } : m))
          );
        };

        const handleStreamReset = () => {
          if (answerHandled || !streamingMessageId) return;
          const targetId = streamingMessageId;
          streamingMessageId = null;
          setMessages((prev) => prev.filter((m) => m.id !== targetId));
          setIsLoading(true);
        };

        const handleAnswer = (data: any) => {
          if (answerHandled) return;
          answerHandled = true;

          const { text: replyText, imageUrl: replyImageUrl, suggestedQuestions, takeaway } =
            extractAssistantReplyFromResponseData(data, imageUrl);

          if (streamingMessageId) {
            const targetId = streamingMessageId;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === targetId
                  ? {
                      ...m,
                      text: replyText,
                      imageUrl: replyImageUrl || m.imageUrl,
                      suggestedQuestions,
                      takeaway,
                      revealTyping: false,
                      isStreaming: false,
                    }
                  : m
              )
            );
            setAssistantTypingDoneId(targetId);
          } else {
            const assistantMessageId = `${Date.now() + 1}`;
            setAssistantTypingDoneId(null);
            setMessages((prev) => [
              ...prev,
              {
                id: assistantMessageId,
                text: replyText,
                isUser: false,
                timestamp: new Date(),
                imageUrl: replyImageUrl || undefined,
                revealTyping: true,
                suggestedQuestions,
                takeaway,
              },
            ]);
            if (!replyText) {
              setAssistantTypingDoneId(assistantMessageId);
            }
          }
          setIsLoading(false);

          if (isVoiceLoopRef.current && pendingVoiceLoopNextRef.current) {
            pendingVoiceLoopNextRef.current = false;
            speakText(replyText);
            const checkSpeechEnd = setInterval(() => {
              if (!window.speechSynthesis.speaking) {
                clearInterval(checkSpeechEnd);
                setTimeout(() => {
                  if (isVoiceLoopRef.current) {
                    sendAfterVoiceRef.current = true;
                    startListening();
                  }
                }, 300);
              }
            }, 200);
          }
        };

        const runAgentRequest = async (sseOptions: Parameters<typeof callDigisakaGptSSE>[0]) => {
          const response = await callDigisakaGptSSE(sseOptions);
          if (response.clientCaptureRequired) {
            const captureReq = response.clientCaptureRequired;
            if (!dashboardScreenshotRef?.current) {
              throw new Error('Dashboard capture is unavailable on this screen.');
            }

            setToolStatuses((prev) =>
              prev.map((s) =>
                s.tool === captureReq.tool || s.tool === CAPTURE_DASHBOARD_SCREENSHOT_TOOL
                  ? { ...s, done: true }
                  : s
              )
            );

            setIsCapturingDashboard(true);
            let captured: string | null = null;
            try {
              captured = await captureDashboardScreenshot(dashboardScreenshotRef.current);
            } finally {
              setIsCapturingDashboard(false);
            }

            if (!captured) {
              throw new Error('Could not capture the dashboard view.');
            }

            setMessages((prev) =>
              prev.map((m) => (m.id === userMessage.id ? { ...m, imageUrl: captured! } : m))
            );

            const toolArgsRaw = captureReq.assistantMessage?.tool_calls as any[] | undefined;
            const matchingCall = toolArgsRaw?.find((tc) => tc?.id === captureReq.toolCallId);
            let focus: string | undefined;
            if (matchingCall?.function?.arguments) {
              try {
                const parsedArgs = JSON.parse(matchingCall.function.arguments);
                focus = typeof parsedArgs?.focus === 'string' ? parsedArgs.focus : undefined;
              } catch {
                focus = undefined;
              }
            }

            const toolResultMessage = {
              role: 'tool',
              tool_call_id: captureReq.toolCallId,
              content: buildDashboardScreenshotToolResult(captured, focus),
            };

            const resumeMessages = activeConversationId
              ? [captureReq.assistantMessage, toolResultMessage]
              : [...requestMessages, captureReq.assistantMessage, toolResultMessage];

            return runAgentRequest({
              conversationId: activeConversationId,
              messages: activeConversationId ? [] : requestMessages,
              farmId: options.farmId ?? fieldContext?.farmId ?? null,
              setToolStatuses,
              thinkingDisabled: !thinkingModeRef.current,
              onAnswerDelta: handleAnswerDelta,
              onStreamReset: handleStreamReset,
              onAnswer: handleAnswer,
              agentResume: true,
              resumeMessages,
            });
          }

          if (!answerHandled && response.data) {
            handleAnswer(response.data);
          }
          return response;
        };

        await runAgentRequest({
          conversationId: activeConversationId,
          messages: requestMessages,
          farmId: options.farmId ?? fieldContext?.farmId ?? null,
          setToolStatuses,
          thinkingDisabled: !thinkingModeRef.current,
          onAnswerDelta: handleAnswerDelta,
          onStreamReset: handleStreamReset,
          onAnswer: handleAnswer,
        });
      } catch (error: any) {
        console.error('API Error:', error);
        let errorText = `Failed to get a response from ${PANDOY_AI_DISPLAY_NAME}.`;
        if (error?.message && typeof error.message === 'string') {
          errorText = error.message;
        }
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            text: errorText,
            isUser: false,
            timestamp: new Date(),
            revealTyping: true,
          },
        ]);
      } finally {
        setIsLoading(false);
        setToolStatuses([]);
        void refreshConversationList();
      }
    },
    [dashboardScreenshotRef, ensureConversationId, fieldContext?.farmId, messages, refreshConversationList]
  );

  // Sync the voice send ref so onSpeechEnd always has the latest sendAgentTurn
  useEffect(() => {
    sendAgentTurnRef.current = sendAgentTurn;
  }, [sendAgentTurn]);

  const handleFieldStarter = useCallback(
    (starter: PandoyStarter) => {
      if (!fieldContext || isLoading) return;
      void sendAgentTurn({
        displayText: starter.label,
        agentContent: starter.prompt,
        farmId: fieldContext.farmId,
      });
    },
    [fieldContext, isLoading, sendAgentTurn]
  );

  const handleRoleStarter = useCallback(
    (starter: PandoyStarter) => {
      if (isLoading || isCapturingDashboard) return;
      void sendAgentTurn({
        displayText: starter.label,
        agentContent: starter.prompt,
        farmId: fieldContext?.farmId ?? null,
      });
    },
    [fieldContext?.farmId, isCapturingDashboard, isLoading, sendAgentTurn]
  );

  const handleSuggestedQuestion = useCallback(
    (question: string) => {
      if (!question.trim() || isLoading) return;
      void sendAgentTurn({
        displayText: question.trim(),
        agentContent: question.trim(),
        farmId: fieldContext?.farmId ?? null,
      });
    },
    [fieldContext?.farmId, isLoading, sendAgentTurn]
  );

  const handleSendMessage = async () => {
    const messageText = inputText.trim();
    if (!messageText && !attachedImage) return;

    const attachSnapshot = attachedImage;

    const userContent = attachSnapshot
      ? [
          {
            type: 'text',
            text:
              messageText ||
              'Please analyze this image for farm, crop, soil or vegetation insights.',
          },
          {
            type: 'image_url',
            image_url: {
              url: attachSnapshot,
            },
          },
        ]
      : messageText;

    setInputText('');
    setAttachedImage(null);
    if (textareaRef.current) textareaRef.current.style.height = '40px';

    await sendAgentTurn({
      displayText: messageText || 'Image attached',
      agentContent: userContent,
      imageUrl: attachSnapshot,
      farmId: fieldContext?.farmId ?? null,
    });
  };

  // Auto-expand after the map has been visible for a while (once per session unless dismissed).
  useEffect(() => {
    if (userDismissedAutoExpandRef.current) return;
    scheduleDelayedOpen(PANDOY_AUTO_POP_DELAY_MS, { respectDismiss: true });
    return cancelAutoExpandTimer;
  }, [cancelAutoExpandTimer, scheduleDelayedOpen]);

  useEffect(() => {
    if (isOpen || isMinimized) cancelAutoExpandTimer();
  }, [cancelAutoExpandTimer, isMinimized, isOpen]);

  // Open once per analysis offer. Map re-renders (time-series slider, chart) must not reset the timer.
  useEffect(() => {
    if (!autoRequest) {
      if (handledAutoRequestRef.current != null) {
        cancelAutoExpandTimer();
      }
      handledAutoRequestRef.current = null;
      return;
    }
    if (handledAutoRequestRef.current === autoRequest) return;
    handledAutoRequestRef.current = autoRequest;

    const paste = autoRequest.pasteText?.trim();
    if (hideCornerLauncherRef.current) return;
    if (!paste) {
      setIsMinimized(false);
      setIsAnimating(true);
      scheduleDelayedOpen(PANDOY_AUTO_POP_DELAY_MS, { respectDismiss: false });
      return;
    }

    cancelAutoExpandTimer();
    openChatPanel();
    setInputText(paste);
    onAutoRequestConsumedRef.current?.();
    window.setTimeout(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }, 50);
  }, [autoRequest, cancelAutoExpandTimer, openChatPanel, scheduleDelayedOpen]);

  const getPromptForSource = (source: AutoRequest['source'], payload?: any) => {
    let base: string;
    switch (source) {
      case 'riceForecast':
        base =
          'The user just generated a rice yield forecast for a specific farm or region. Using this dashboard screenshot and the numeric API data below, explain the forecast, highlight any risks or opportunities, and give clear agronomy advice.';
        break;
      case 'heatMap':
        base =
          'The user just generated an index map analysis for a field. Using this dashboard screenshot and the numeric API data below, explain what the map shows and give farm-level recommendations.';
        break;
      case 'farmWalk':
        base =
          `The farmer finished an Index Map farm walk${
            payload?.farmId ? ` (Farm ID: ${payload.farmId})` : ''
          }. IMPORTANT: All evidence is already in the JSON below under userInputs (farmer vegetation/moisture answers) and systemFetched (satellite index/NDVI/NDMI samples already taken at each GPS stop during the walk). Use ONLY that data plus the dashboard screenshot. Do NOT call get_field_health, get_vegetation_history, get_anomaly, or any other tool — do not re-fetch satellite or field data. Compare userInputs vs systemFetched stop-by-stop, note agreements/disagreements in plain language, then give practical next steps.`;
        break;
      case 'timeSeries':
        base =
          `The user just generated a time series analysis for a field${payload?.farmId ? ` (Farm ID: ${payload.farmId})` : ''}. Using this dashboard screenshot and the numeric API data below, explain the trends and give farm-level recommendations.`;
        break;
      case 'strawBurn':
        base =
          'The user just viewed straw burn detection results. Using this dashboard screenshot and the numeric API data below, summarize findings and give recommendations.';
        break;
      case 'treeDetection':
        base =
          'The user just ran tree detection on a field. Using this dashboard screenshot and the numeric API data below, explain the results and any management recommendations.';
        break;
      case 'crownSegmentation':
        base =
          'The user just ran crown segmentation analysis. Using this dashboard screenshot and the numeric API data below, explain the results and recommendations.';
        break;
      case 'cropfieldDelineation':
        base =
          'The user just ran AI field boundary delineation. Using this dashboard screenshot and the numeric API data below, explain the detected field boundaries, comment on the number of fields found, their confidence scores, and give practical recommendations on how to use or refine these boundaries for farm management.';
        break;
      case 'bsri':
        base =
          'The user just requested a Black Sigatoka Risk (BSRI) multi-temporal analysis for banana plantations. Using this dashboard screenshot and the date range below, explain what you can infer and give management recommendations.';
        break;
      case 'plantingMethod':
        base =
          'The user just enabled the rice planting method multi-temporal layer. Using this dashboard screenshot, explain what the planting patterns imply and give practical recommendations.';
        break;
      case 'climate':
        base =
          `The user just ran a climate analysis for a field${payload?.farmId ? ` (Farm ID: ${payload.farmId})` : ''}. Using this dashboard screenshot and the numeric API data below, explain the climate conditions and their impact on crop performance, and give farm-level recommendations.`;
        break;
      case 'cropCalendar':
      case 'other':
      default:
        base =
          'Please analyze this dashboard view for farm-level insights and give clear recommendations, using the numeric API data below where helpful.';
        break;
    }

    let numericPart = '';
    if (payload) {
      try {
        // Farm walk: send compact user + system blocks; drop nested stops duplicate / map clutter
        const payloadForPrompt =
          source === 'farmWalk'
            ? {
                type: payload.type,
                farmId: payload.farmId,
                farmName: payload.farmName,
                startedAt: payload.startedAt,
                endedAt: payload.endedAt,
                userInputs: payload.userInputs,
                systemFetched: payload.systemFetched,
                summary: payload.summary,
              }
            : payload;

        const json = JSON.stringify(
          payloadForPrompt,
          (key, value) => {
            // Drop obvious non-numeric / non-semantic clutter
            if (
              key === 'tile_url' ||
              key === 'tileUrl' ||
              key === 'url' ||
              key === 'tiles' ||
              key === 'tile' ||
              key === 'geometry' ||
              key === 'visParams' ||
              key === 'palette' ||
              key === 'coordinates' ||
              key === 'indexMap'
            ) {
              return undefined;
            }
            if (typeof value === 'string' && value.startsWith('http')) {
              return undefined;
            }
            // Keep short strings (e.g. dates, index names), drop very long ones
            if (typeof value === 'string' && value.length > 120) {
              return '[long_string_truncated]';
            }
            return value;
          },
          2
        );
        numericPart =
          source === 'farmWalk'
            ? '\n\nPre-collected farm walk evidence (userInputs = farmer answers; systemFetched = satellite samples already computed — do not re-run tools):\n' +
              json
            : '\n\nHere is the raw API response data (focus on numeric fields like indices, areas, yields, confidences, dates, and the farmId which identifies the specific farm being analysed; ignore URLs and tile references):\n' +
              json;
      } catch {
        // ignore JSON errors
      }
    }

    return base + numericPart;
  };

  const getChatLabelForSource = (source: AutoRequest['source']) => {
    switch (source) {
      case 'riceForecast':
        return 'Looking at the rice harvest outlook…';
      case 'heatMap':
        return 'Looking at the field map…';
      case 'farmWalk':
        return 'Comparing your farm-walk notes with the satellite view…';
      case 'timeSeries':
        return 'Looking at how the field has changed over time…';
      case 'strawBurn':
        return 'Checking for burned straw in the field…';
      case 'treeDetection':
        return 'Looking at trees in the field…';
      case 'crownSegmentation':
        return 'Looking at tree crowns in the field…';
      case 'cropfieldDelineation':
        return 'Looking at the field boundaries…';
      case 'bsri':
        return 'Checking banana leaf-spot risk…';
      case 'plantingMethod':
        return 'Looking at how rice was planted…';
      case 'cropCalendar':
        return 'Looking at the crop calendar…';
      case 'climate':
        return 'Looking at rain and temperature for this field…';
      case 'other':
      default:
        return 'Looking at what’s on your screen…';
    }
  };
  const handleAnalyseWithGptClick = async () => {
    if (!autoRequest || !screenshotTargetRef?.current || isLoading) {
      console.log('[Digisaka GPT] Analyse click skipped', {
        hasAutoRequest: !!autoRequest,
        hasRef: !!screenshotTargetRef?.current,
        isLoading,
      });
      return;
    }
    const request = autoRequest;
    onAutoRequestConsumed?.();

    try {
      // Always start a fresh chat for every dashboard analysis
      console.log('[Digisaka GPT] Starting new chat for analysis...');
      setRenamingConversationId(null);
      setDeleteConfirmId(null);
      setRenameDraft('');
      setInputText('');
      if (textareaRef.current) textareaRef.current.style.height = '40px';

      let newConversationId: string | null = null;
      try {
        const { id } = await createDigisakaGptConversation();
        newConversationId = id;
        sessionStorage.setItem(DIGISAKA_GPT_CONVERSATION_STORAGE_KEY, id);
        setConversationId(id);
        setMessages([]);
        await refreshConversationList();
      } catch {
        setConversationId(null);
        setMessages([]);
      }

      console.log('[Digisaka GPT] Capturing screenshot...');
      setIsAutoAnalysing(true);
      const html2canvasModule = await import('html2canvas');
      const canvas = await html2canvasModule.default(screenshotTargetRef.current, {
        useCORS: true,
        logging: false,
        scale: 1,
      });
      const dataUrl = canvas.toDataURL('image/png');
      const promptPrefix = getPromptForSource(request.source, request.payload);
      const chatLabel = getChatLabelForSource(request.source);

      const userMessage: Message = {
        id: Date.now().toString(),
        // Show short, friendly label in UI; keep full prompt only for backend.
        text: chatLabel,
        isUser: true,
        timestamp: new Date(),
        imageUrl: dataUrl,
      };
      setMessages((prev) => [...prev, userMessage]);
      setAttachedImage(null);
      setInputText('');
      setIsLoading(true);

      console.log('[Digisaka GPT] Calling Digisaka GPT backend...');
      const multimodalContent: any[] = [
        { type: 'text', text: promptPrefix },
        { type: 'image_url', image_url: { url: dataUrl } },
      ];

      // New chat: send only the current analysis message (no prior history)
      const requestMessages: { role: 'user' | 'assistant'; content: any }[] = [
        { role: 'user', content: multimodalContent },
      ];

      const farmIdFromPayload =
        request.payload?.farmId != null ? String(request.payload.farmId) : fieldContext?.farmId ?? null;

      setToolStatuses([]);
      let analyseAnswerHandled = false;
      let analyseStreamingMessageId: string | null = null;

      const handleAnalyseAnswerDelta = (delta: string) => {
        if (!delta || analyseAnswerHandled) return;
        if (!analyseStreamingMessageId) {
          analyseStreamingMessageId = `${Date.now() + 1}`;
          setAssistantTypingDoneId(null);
          setMessages((prev) => [
            ...prev,
            {
              id: analyseStreamingMessageId!,
              text: delta,
              isUser: false,
              timestamp: new Date(),
              revealTyping: false,
              isStreaming: true,
            },
          ]);
          setIsLoading(false);
          return;
        }
        const targetId = analyseStreamingMessageId;
        setMessages((prev) =>
          prev.map((m) => (m.id === targetId ? { ...m, text: `${m.text}${delta}` } : m))
        );
      };

      const handleAnalyseStreamReset = () => {
        if (analyseAnswerHandled || !analyseStreamingMessageId) return;
        const targetId = analyseStreamingMessageId;
        analyseStreamingMessageId = null;
        setMessages((prev) => prev.filter((m) => m.id !== targetId));
        setIsLoading(true);
      };

      const handleAnalyseAnswer = (data: any) => {
        if (analyseAnswerHandled) return;
        analyseAnswerHandled = true;
        const { text: replyText, suggestedQuestions, takeaway } =
          extractAssistantReplyFromResponseData(data);
        if (analyseStreamingMessageId) {
          const targetId = analyseStreamingMessageId;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === targetId
                ? {
                    ...m,
                    text: replyText,
                    suggestedQuestions,
                    takeaway,
                    revealTyping: false,
                    isStreaming: false,
                  }
                : m
            )
          );
          setAssistantTypingDoneId(targetId);
        } else {
          const assistantMessageId = `${Date.now() + 1}`;
          setAssistantTypingDoneId(null);
          setMessages((prev) => [
            ...prev,
            {
              id: assistantMessageId,
              text: replyText,
              isUser: false,
              timestamp: new Date(),
              revealTyping: true,
              suggestedQuestions,
              takeaway,
            },
          ]);
          if (!replyText) {
            setAssistantTypingDoneId(assistantMessageId);
          }
        }
        setIsLoading(false);
      };

      const response = await callDigisakaGptSSE({
        conversationId: newConversationId,
        messages: requestMessages,
        farmId: farmIdFromPayload,
        setToolStatuses,
        thinkingDisabled: !thinkingModeRef.current,
        onAnswerDelta: handleAnalyseAnswerDelta,
        onStreamReset: handleAnalyseStreamReset,
        onAnswer: handleAnalyseAnswer,
      });

      if (!analyseAnswerHandled) {
        handleAnalyseAnswer(response.data);
      }
    } catch (e) {
      console.error('[Digisaka GPT] Analyse failed:', e);
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          text: (e as any)?.message ?? 'Analysis failed.',
          isUser: false,
          timestamp: new Date(),
          revealTyping: true,
        },
      ]);
      setShowAutoSuccess(true);
      setTimeout(() => setShowAutoSuccess(false), 2000);
    } finally {
      setIsLoading(false);
      setIsAutoAnalysing(false);
      setToolStatuses([]);
      void refreshConversationList();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Corner launcher stays off while Settings or the Pandoy page is open. */}
      {/* Minimized State - Small Button on Right */}
      {isMinimized && !hideCornerLauncher && chatLayout !== 'page' && (
        <div data-digisaka-gpt-ui className="fixed bottom-6 right-0 z-[90] flex items-center">
          <div 
            className="relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            {/* Tooltip */}
            {showTooltip && (
              <div
                className={`absolute right-12 top-1/2 transform -translate-y-1/2 text-xs px-3 py-2 rounded-lg whitespace-nowrap shadow-lg z-50 ${
                  isDark ? 'bg-gray-900 text-white border border-gray-700' : 'bg-white text-gray-900 border border-gray-200'
                }`}
              >
                {hasUnseenAnalyseOffer ? 'New analysis ready' : 'Show Pandoy AI'}
                <div className="absolute right-0 top-1/2 transform translate-x-full -translate-y-1/2">
                  <div
                    className={`border-4 border-transparent ${isDark ? 'border-l-gray-900' : 'border-l-white'}`}
                  ></div>
                </div>
              </div>
            )}
            
            {/* Small Restore Button */}
            <button
              onClick={handleRestore}
              className={`relative w-8 h-20 rounded-l-lg shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 text-white ${
                isDark ? 'bg-gray-600 hover:bg-gray-500' : ''
              } ${hasUnseenAnalyseOffer ? 'ring-2 ring-emerald-300 ring-offset-2 ring-offset-transparent' : ''}`}
              style={isDark ? undefined : { backgroundColor: BRAND_GREEN }}
              aria-label={hasUnseenAnalyseOffer ? 'Pandoy AI — new analysis ready' : 'Show Pandoy AI'}
            >
              {hasUnseenAnalyseOffer && (
                <span className="absolute -top-1 -left-1 z-10 flex h-3.5 w-3.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75 motion-reduce:animate-none" />
                  <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-emerald-400" />
                </span>
              )}
              <i className="ri-arrow-left-line text-white text-lg"></i>
            </button>
          </div>
        </div>
      )}

      {/* Floating Logo Button - hidden on the Pandoy page and on Settings. */}
      {!isMinimized && !hideCornerLauncher && chatLayout !== 'page' && (
        <div
          data-digisaka-gpt-ui
          data-tour-id="digisaka-gpt"
          className="fixed bottom-3 right-3 md:bottom-4 md:right-[18px] z-[90] flex flex-col items-end space-y-2"
        >
          {/* Main Logo Button — click to open/close the chat popup. */}
          <div className="relative">
            <button
              type="button"
              onClick={handleToggleChat}
              className={`relative z-0 flex flex-col items-center gap-1 bg-transparent p-0 transition-transform duration-150 hover:scale-105 ${
                isAnimating ? 'scale-105' : ''
              }`}
              aria-label={
                isOpen
                  ? 'Close Pandoy AI'
                  : autoPopCountdownSec != null && autoPopCountdownSec > 0
                    ? `Pandoy AI opens in ${autoPopCountdownSec} seconds`
                    : hasUnseenAnalyseOffer
                      ? 'Pandoy AI — new analysis ready'
                      : 'Open Pandoy AI'
              }
            >
              <span className="relative flex h-14 w-14 shrink-0 items-center justify-center">
                {autoPopCountdownSec != null && autoPopCountdownSec > 0 && !isOpen && (
                  <span
                    className="pointer-events-none absolute inset-0 rounded-full border-2 border-white/70 motion-reduce:animate-none animate-pulse"
                    aria-hidden
                  />
                )}
                <img
                  src={digisakaGptLogo}
                  alt=""
                  className={`h-14 w-14 object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,.35)] ${
                    autoPopCountdownSec != null && autoPopCountdownSec > 0 && !isOpen
                      ? 'motion-reduce:animate-none animate-pulse'
                      : ''
                  }`}
                />
                {autoPopCountdownSec != null && autoPopCountdownSec > 0 && !isOpen ? (
                  <span className="absolute -top-0.5 -right-0.5 z-20 flex h-5 min-w-5 items-center justify-center px-0.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75 motion-reduce:animate-none" />
                    <span className="relative inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold tabular-nums text-[#008540] shadow-md">
                      {autoPopCountdownSec}
                    </span>
                  </span>
                ) : hasUnseenAnalyseOffer ? (
                  <span className="absolute -top-0.5 -right-0.5 z-20 flex h-4 w-4 items-center justify-center">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75 motion-reduce:animate-none" />
                    <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-white text-[9px] font-bold text-[#008540] shadow-md">
                      1
                    </span>
                  </span>
                ) : null}
              </span>
              <span
                className="whitespace-nowrap text-center text-[10px] font-bold leading-tight text-white [text-shadow:0_1px_3px_rgba(0,0,0,.65)]"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                Pandoy AI
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Fullscreen focus backdrop — blurs dashboard behind the popup only. */}
      {isOpen && isChatFullscreen && chatLayout !== 'page' && (
        <button
          type="button"
          data-digisaka-gpt-ui
          className="fixed inset-0 z-[89] bg-black/40 backdrop-blur-md transition-opacity duration-200 cursor-default"
          onClick={() => setIsChatFullscreen(false)}
          aria-label="Exit full screen"
        />
      )}

      {/* Chat Modal. The small popup stays hidden while Settings is open. */}
      {isOpen && (chatLayout === 'page' || !hideCornerLauncher) && (
        <div
          data-digisaka-gpt-ui
          className={`fixed z-[90] flex flex-col overflow-hidden font-sans ${pd.panel} ${
            chatLayout === 'page'
              ? 'inset-0 rounded-none border-0 lg:left-[var(--nav-sidebar-w)] lg:top-[68px]'
              : isChatFullscreen
              ? 'inset-x-2 bottom-2 top-24 rounded-[20px] md:inset-x-6 md:bottom-6 md:top-14 lg:top-14'
              : `pandoy-chat-panel left-2 right-2 md:left-auto md:right-[18px] bottom-[6.25rem] md:bottom-[6rem] h-[min(52vh,calc(100dvh-8.5rem))] md:h-[min(480px,calc(100dvh-8.5rem))] rounded-[20px] ${
                  chatSidebarOpen ? 'md:w-[42rem] max-w-[calc(100vw-1.5rem)]' : 'md:w-[min(100%,24rem)]'
                }`
          }`}
        >
          <div className="flex flex-1 min-h-0 relative">
            {chatSidebarOpen && !isChatFullscreen && chatLayout !== 'page' && (
              <button
                type="button"
                className="absolute inset-0 z-10 bg-black/40 md:hidden"
                onClick={() => setChatSidebarOpen(false)}
                aria-label="Close chat sidebar"
              />
            )}

            <aside
              className={`flex flex-col shrink-0 z-20 h-full transition-all duration-200 ease-out ${
                chatSidebarOpen ? 'w-[230px]' : 'w-0 overflow-hidden border-0'
              } ${
                !isChatFullscreen && chatLayout !== 'page' && chatSidebarOpen
                  ? 'absolute left-0 top-0 bottom-0 shadow-2xl'
                  : 'relative'
              } ${pd.aside}`}
              aria-hidden={!chatSidebarOpen}
            >
              {chatSidebarOpen && (
                <GptChatSidebar
                  isDark={isDark}
                  conversations={conversationList}
                  activeId={conversationId}
                  searchQuery={chatSearchQuery}
                  onSearchChange={setChatSearchQuery}
                  onSelect={(id) => void switchConversation(id)}
                  onNewChat={() => void handleResetChat()}
                  onClose={!isChatFullscreen && chatLayout !== 'page' ? () => setChatSidebarOpen(false) : undefined}
                  disabled={conversationHydrating || isLoading || chatActionBusy}
                  renamingId={renamingConversationId}
                  renameDraft={renameDraft}
                  onRenameDraftChange={setRenameDraft}
                  onStartRename={(id, title) => {
                    setRenamingConversationId(id);
                    setRenameDraft(title);
                    setDeleteConfirmId(null);
                  }}
                  onCancelRename={() => {
                    setRenamingConversationId(null);
                    setRenameDraft('');
                  }}
                  onSubmitRename={(id) => void handleRenameConversation(id, renameDraft)}
                  deleteConfirmId={deleteConfirmId}
                  onRequestDelete={(id) => {
                    setDeleteConfirmId(id);
                    setRenamingConversationId(null);
                  }}
                  onCancelDelete={() => setDeleteConfirmId(null)}
                  onConfirmDelete={(id) => void handleDeleteConversation(id)}
                />
              )}
            </aside>

            <div className="relative flex flex-col flex-1 min-w-0 min-h-0">
          {/* Top bar — white surface + Digisaka logo (reference mock) */}
          <div
            className={`shrink-0 flex flex-col gap-2 border-b px-4 py-2 md:px-5 md:py-2.5 ${pd.header}`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <button
                type="button"
                onClick={() => setChatSidebarOpen((v) => !v)}
                className={`shrink-0 rounded-md p-1.5 transition-colors ${pd.iconBtn}`}
                aria-label={chatSidebarOpen ? 'Hide chat list' : 'Show chat list'}
                aria-expanded={chatSidebarOpen}
              >
                <i
                  className={`text-[17px] ${chatSidebarOpen ? 'ri-sidebar-fold-line' : 'ri-sidebar-unfold-line'}`}
                  aria-hidden
                />
              </button>
              <img
                src={digisakaGptLogo}
                alt="Pandoy AI"
                className="w-8 h-8 object-contain shrink-0"
              />
              <h3
                className={`font-semibold text-[15px] truncate ${pd.heading}`}
              >
                Pandoy AI
              </h3>
              {/* Fast / Thinking toggle */}
              <button
                type="button"
                onClick={() => setThinkingMode((prev) => !prev)}
                className={`ml-2 shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium transition-colors border ${
                  thinkingMode ? pd.thinkingOn : pd.thinkingOff
                }`}
                title={
                  thinkingMode
                    ? 'Thinking mode (slower, shows reasoning). Click for Fast mode.'
                    : 'Fast mode (quick responses). Click for Thinking mode.'
                }
              >
                {thinkingMode ? (
                  <>
                    <i className="ri-brain-line text-[9px] mr-0.5" />
                    Thinking
                  </>
                ) : (
                  <>
                    <i className="ri-flashlight-line text-[9px] mr-0.5" />
                    Fast
                  </>
                )}
              </button>
              <div className="ml-auto flex items-center shrink-0 gap-1">
                <button
                  type="button"
                  onClick={() => {
                    setMemoryOpen((open) => !open);
                    if (!memoryOpen) void refreshMemories();
                  }}
                  className={`rounded-md p-1.5 transition-colors ${memoryOpen ? (isDark ? 'text-[#34d399]' : 'text-[#008540]') : pd.iconBtn}`}
                  aria-label="Saved memory"
                  title="What Pandoy remembers about you"
                >
                  <i className="ri-bookmark-line text-[17px]" />
                </button>
                {chatLayout !== 'page' && (
                <button
                  type="button"
                  onClick={() => setIsChatFullscreen((prev) => !prev)}
                  className={`rounded-md p-1.5 transition-colors ${pd.iconBtn}`}
                  aria-label={isChatFullscreen ? 'Exit full screen' : 'Full screen'}
                >
                  <i
                    className={
                      isChatFullscreen ? 'ri-fullscreen-exit-line text-[17px]' : 'ri-fullscreen-line text-[17px]'
                    }
                  />
                </button>
                )}
                <button
                  type="button"
                  onClick={handleToggleChat}
                  className={`rounded-md p-1.5 transition-colors ${pd.iconBtn}`}
                  aria-label="Close chat"
                >
                  <i className="ri-close-line text-[17px]" />
                </button>
              </div>
            </div>
            {fieldContext && (
              <div
                className={`flex items-center gap-1.5 text-[11px] rounded-lg px-2.5 py-1 w-fit max-w-full border ${pd.fieldChip}`}
              >
                <i className="ri-map-pin-2-line text-xs shrink-0" aria-hidden />
                <span className="truncate">
                  Field: {fieldContext.farmName?.trim() || fieldContext.farmId}
                </span>
              </div>
            )}
          </div>

          {memoryOpen && (
            <div
              className={`absolute inset-0 z-30 flex flex-col px-5 py-4 ${
                isDark ? 'bg-[#161b22] text-[#e6edf3]' : 'bg-[#f7f8f4] text-[#111827]'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold">Memory</h4>
                  <p className={`mt-1 text-[11px] leading-relaxed ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>
                    Pandoy keeps short facts you ask it to remember, and uses them in later chats.
                    Say “remember that I grow rice” or “forget that”.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setMemoryOpen(false)}
                  className={`rounded-md p-1.5 ${pd.iconBtn}`}
                  aria-label="Close memory"
                >
                  <i className="ri-close-line text-[17px]" />
                </button>
              </div>
              <div className="mt-4 min-h-0 flex-1 overflow-y-auto space-y-2">
                {memoryBusy && memories.length === 0 ? (
                  <p className={`text-xs ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>Loading…</p>
                ) : memories.length === 0 ? (
                  <p className={`text-xs ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>
                    Nothing saved yet.
                  </p>
                ) : (
                  memories.map((item) => (
                    <div
                      key={item.id}
                      className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${
                        isDark ? 'border-white/10 bg-[#1c2432]' : 'border-[#e5e7eb] bg-white'
                      }`}
                    >
                      <p className="flex-1 text-xs leading-relaxed">{item.content}</p>
                      <button
                        type="button"
                        disabled={memoryBusy}
                        onClick={() => void handleDeleteMemory(item.id)}
                        className="shrink-0 text-[11px] text-red-600 disabled:opacity-50"
                      >
                        Forget
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Messages Area */}
          <div
            className={`flex-1 min-h-0 overflow-y-auto px-5 py-5 space-y-3.5 relative ${pd.messages}`}
          >
            {/* Messages Content */}
            <div className="relative z-10">
            {autoRequest && !autoRequest.pasteText && messages.length === 0 && (
              <div className="flex justify-start">
                <GptAnalyseOfferCard
                  className="max-w-full"
                  isDark={isDark}
                  onAccept={handleAnalyseWithGptClick}
                  onDismiss={() => onAutoRequestConsumedRef.current?.()}
                  isLoading={isLoading}
                  isAutoAnalysing={isAutoAnalysing}
                  disabled={!screenshotTargetRef?.current}
                  title={
                    autoRequest.source === 'farmWalk'
                      ? 'Validate farm walk with Pandoy AI?'
                      : 'Analyse with Pandoy AI?'
                  }
                  description={
                    autoRequest.source === 'farmWalk'
                      ? 'Pandoy AI will compare your walk answers with the satellite samples collected during the walk.'
                      : 'Pandoy AI will reason about your map view and current results to answer your questions. You can also run analyses directly from the chat.'
                  }
                />
              </div>
            )}
            {fieldContext && !autoRequest && messages.length === 0 && (
              <div className="flex justify-start">
                <GptFieldQuickActionsCard
                  fieldContext={fieldContext}
                  starters={fieldStarters}
                  isDark={isDark}
                  isLoading={isLoading || isCapturingDashboard}
                  onStarter={handleFieldStarter}
                />
              </div>
            )}
            {!fieldContext && !autoRequest && messages.length === 0 && (
              <GptDashboardAnalyzeCard
                isDark={isDark}
                disabled={isLoading || isCapturingDashboard}
                starters={roleStarters}
                onStarter={handleRoleStarter}
              />
            )}
            {messages.length > 0 &&
              messages.map((message) => {
                const replyReady =
                  !message.isUser &&
                  message.id === lastAssistantMessageId &&
                  !isLoading &&
                  !message.isStreaming &&
                  (assistantTypingDoneId === message.id || !message.revealTyping);
                const showSuggestedQuestions =
                  replyReady && (message.suggestedQuestions?.length ?? 0) > 0;
                const activeChat = conversationList.find((c) => c.id === conversationId);

                return (
                  <Fragment key={message.id}>
                    <div className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[88%] px-4 py-3 ${
                          message.isUser ? pd.userBubble : pd.assistantBubble
                        }`}
                      >
                        {message.text && message.isUser && (
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
                        )}
                        {message.text && !message.isUser && (
                          <GptAssistantMarkdown
                            text={message.text}
                            revealTyping={message.revealTyping}
                            isStreaming={message.isStreaming}
                            isDark={isDark}
                            onTypingTick={scrollChatToBottom}
                            onTypingComplete={() => setAssistantTypingDoneId(message.id)}
                          />
                        )}
                    {message.imageUrl && (
                      <div className="mt-2">
                        <img
                          src={message.imageUrl}
                          alt={message.isUser ? 'Attached image' : 'AI response'}
                          className={`max-h-40 rounded-md cursor-pointer object-contain border ${
                            isDark ? 'border-gray-600' : 'border-gray-200'
                          }`}
                          onClick={() => setFullScreenImage(message.imageUrl || null)}
                        />
                        <p className={`text-[11px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Click to view full screen
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1.5">
                      <p
                        className={`text-[11px] ${
                          message.isUser
                            ? 'text-white/75'
                            : isDark
                              ? 'text-gray-500'
                              : 'text-[#6b8577]'
                        }`}
                      >
                        {message.timestamp.toLocaleTimeString()}
                      </p>
                      {!message.isUser && ttsSupported && (
                        <button
                          type="button"
                          onClick={() => {
                            if (isSpeaking && lastSpokenMessageId === message.id) {
                              stopSpeaking();
                              setLastSpokenMessageId(null);
                            } else {
                              stopSpeaking();
                              speakText(message.text);
                              setLastSpokenMessageId(message.id);
                            }
                          }}
                          className={`text-[11px] p-0.5 rounded transition-colors ${
                            isSpeaking && lastSpokenMessageId === message.id
                              ? isDark
                                ? 'text-[#34d399]'
                                : 'text-[#008540]'
                              : isDark
                                ? 'text-[#8b949e] hover:text-[#e6edf3]'
                                : 'text-[#6b7280] hover:text-[#008540]'
                          }`}
                          title={
                            isSpeaking && lastSpokenMessageId === message.id
                              ? 'Stop speaking'
                              : 'Read aloud'
                          }
                        >
                          <i
                            className={`text-sm ${
                              isSpeaking && lastSpokenMessageId === message.id
                                ? 'ri-volume-up-fill'
                                : 'ri-volume-up-line'
                            }`}
                          />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                    {replyReady && (
                      <div className="mt-2 max-w-[88%] space-y-2">
                        {message.takeaway && (
                          <div className="rounded-lg bg-[#e7f6ee] px-3 py-2.5 text-[12px] font-bold uppercase leading-snug tracking-wide text-[#0f6b38]">
                            {message.takeaway}
                          </div>
                        )}
                        <p className={`text-[11px] ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>
                          Pin this chat or mark it with a star so you can find it again.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={!conversationId || chatActionBusy}
                            onClick={() => void handleConversationFlag('pinned')}
                            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                              activeChat?.pinned
                                ? 'border-[#008540] bg-[#e7f6ee] text-[#0f6b38]'
                                : isDark
                                  ? 'border-white/10 text-[#e6edf3]'
                                  : 'border-[#e5e7eb] bg-white text-[#111827]'
                            }`}
                          >
                            <i className={activeChat?.pinned ? 'ri-pushpin-fill' : 'ri-pushpin-line'} aria-hidden />
                            {activeChat?.pinned ? 'Pinned' : 'Pin chat'}
                          </button>
                          <button
                            type="button"
                            disabled={!conversationId || chatActionBusy}
                            onClick={() => void handleConversationFlag('is_favourite')}
                            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                              activeChat?.is_favourite
                                ? 'border-amber-400 bg-amber-50 text-amber-700'
                                : isDark
                                  ? 'border-white/10 text-[#e6edf3]'
                                  : 'border-[#e5e7eb] bg-white text-[#111827]'
                            }`}
                          >
                            <i className={activeChat?.is_favourite ? 'ri-star-fill' : 'ri-star-line'} aria-hidden />
                            {activeChat?.is_favourite ? 'Favourite' : 'Add to favourites'}
                          </button>
                        </div>
                      </div>
                    )}
                    {showSuggestedQuestions && message.suggestedQuestions && (
                      <GptSuggestedQuestionsCard
                        questions={message.suggestedQuestions}
                        isDark={isDark}
                        disabled={isLoading}
                        onSelect={handleSuggestedQuestion}
                      />
                    )}
                  </Fragment>
                );
              })}

            {/* Tool calling status indicators */}
            {isLoading && toolStatuses.length > 0 && (
              <div className="flex justify-start">
                <div
                  className={`max-w-[88%] border rounded-[4px_14px_14px_14px] px-4 py-3 shadow-sm space-y-1.5 ${
                    isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-[rgba(30,80,50,0.12)]'
                  }`}
                >
                  {toolStatuses.map((ts, idx) => (
                    <div key={`${ts.tool}-${idx}`} className="flex items-center gap-2 text-xs">
                      {ts.done ? (
                        <span
                          className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 text-white ${
                            isDark ? 'bg-gray-500' : ''
                          }`}
                          style={isDark ? undefined : { backgroundColor: BRAND_GREEN }}
                        >
                          <i className="ri-check-line text-[10px]" />
                        </span>
                      ) : (
                        <span
                          className={`w-4 h-4 rounded-full border-2 border-t-transparent animate-spin flex-shrink-0 ${
                            isDark ? 'border-[#34d399]' : 'border-[#008540]'
                          }`}
                        />
                      )}
                      <span
                        className={
                          ts.done
                            ? isDark
                              ? 'text-gray-500 line-through'
                              : 'text-gray-400 line-through'
                            : isDark
                              ? 'text-gray-200 font-medium'
                              : 'text-gray-700 font-medium'
                        }
                      >
                        {ts.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Capturing dashboard screenshot */}
            {isCapturingDashboard && (
              <div className="flex justify-start">
                <div
                  className={`max-w-[88%] rounded-[4px_14px_14px_14px] border px-4 py-3 text-xs ${pd.assistantBubble}`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-4 w-4 rounded-full border-2 border-t-transparent animate-spin ${
                        isDark ? 'border-[#34d399]' : 'border-[#008540]'
                      }`}
                      aria-hidden
                    />
                    <span>Looking at what’s on your screen…</span>
                  </div>
                </div>
              </div>
            )}

            {/* Loading: until first tool_start SSE — Pandoy logo + ring animation */}
            {isLoading && !isCapturingDashboard && toolStatuses.length === 0 && (
              <GptPandoyWaitingIndicator isDark={isDark} thinkingMode={thinkingMode} />
            )}

              {/* Scroll anchor */}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input area — reference mock: white bar + rounded input row */}
          <div
            className={`shrink-0 px-5 pt-3 pb-3.5 border-t transition-colors ${pd.composer} ${isImageDragOver ? (isDark ? 'ring-2 ring-[#34d399]/40 ring-inset' : 'ring-2 ring-[#008540]/30 ring-inset bg-[#f3f4f6]/50') : ''}`}
            onDragOver={handleImageDropZoneDragOver}
            onDragLeave={handleImageDropZoneDragLeave}
            onDrop={handleImageDrop}
          >
            {isImageDragOver && (
              <div className={`pointer-events-none text-center text-xs font-medium py-1 ${isDark ? 'text-[#34d399]' : 'text-[#008540]'}`}>
                Drop image here
              </div>
            )}
            {attachedImage && (
              <div
                className={`flex items-center justify-between border rounded-[10px] p-2 ${pd.attach}`}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={`w-10 h-10 rounded-md overflow-hidden border ${
                      isDark ? 'border-[#2d3748] bg-[#161b22]' : 'border-[#e5e7eb] bg-white'
                    }`}
                  >
                    <img
                      src={attachedImage}
                      alt="Attached"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className={`text-xs ${pd.muted}`}>
                    Image attached. It will be sent with your next message.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setAttachedImage(null)}
                  className={`text-xs ${isDark ? 'text-[#8b949e] hover:text-[#e6edf3]' : 'text-[#008540] hover:opacity-80'}`}
                >
                  Remove
                </button>
              </div>
            )}
            <div
              className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 ${pd.inputWrap}`}
            >
              <label
                className={`shrink-0 cursor-pointer p-0.5 transition-colors ${pd.iconBtn}`}
                title="Attach image from your device"
              >
                <i className="ri-image-line text-lg" />
                <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
              </label>

              {/* Voice input button (microphone) with sound-wave visualizer */}
              {sttSupported && (
                <button
                  type="button"
                  onClick={() => {
                    if (isListening) {
                      sendAfterVoiceRef.current = false;
                      stopListening();
                    } else {
                      sendAfterVoiceRef.current = true;
                      startListening();
                    }
                  }}
                  className={`shrink-0 relative flex items-center justify-center w-7 h-7 rounded-lg transition-colors ${
                    isListening
                      ? 'text-red-500 bg-red-500/10'
                      : isDark
                        ? 'text-[#8b949e] hover:text-[#e6edf3] hover:bg-white/[0.06]'
                        : 'text-[#6b7280] hover:text-[#008540] hover:bg-[#f3f4f6]'
                  }`}
                  title={isListening ? 'Stop recording' : 'Voice input'}
                >
                  {/* Sound-wave bars — only visible when listening */}
                  {isListening ? (
                    <div className="flex items-center justify-center gap-[2px] h-4 w-4">
                      <span className="voice-wave-bar h-3 w-[2px] rounded-full" />
                      <span className="voice-wave-bar h-4 w-[2px] rounded-full" style={{ animationDelay: '0.15s' }} />
                      <span className="voice-wave-bar h-2 w-[2px] rounded-full" style={{ animationDelay: '0.3s' }} />
                      <span className="voice-wave-bar h-4 w-[2px] rounded-full" style={{ animationDelay: '0.45s' }} />
                      <span className="voice-wave-bar h-3 w-[2px] rounded-full" style={{ animationDelay: '0.6s' }} />
                    </div>
                  ) : (
                    <i className="ri-mic-line text-base" />
                  )}
                </button>
              )}

              {/* Hands-free voice loop toggle */}
              {sttSupported && ttsSupported && (
                <button
                  type="button"
                  onClick={() => {
                    if (isVoiceLoop) {
                      // Turn off: stop listening, stop speaking, exit mode
                      setIsVoiceLoop(false);
                      isVoiceLoopRef.current = false;
                      pendingVoiceLoopNextRef.current = false;
                      sendAfterVoiceRef.current = false;
                      stopListening();
                      stopSpeaking();
                    } else {
                      // Turn on: start listening in loop mode
                      setIsVoiceLoop(true);
                      sendAfterVoiceRef.current = true;
                      startListening();
                    }
                  }}
                  className={`shrink-0 relative flex items-center justify-center w-7 h-7 rounded-lg transition-colors ${
                    isVoiceLoop
                      ? 'text-[#34d399] bg-[rgba(52,211,153,.12)] ring-1 ring-[#34d399]/40'
                      : isDark
                        ? 'text-[#8b949e] hover:text-[#e6edf3] hover:bg-white/[0.06]'
                        : 'text-[#6b7280] hover:text-[#008540] hover:bg-[#f3f4f6]'
                  }`}
                  title={
                    isVoiceLoop
                      ? 'Disable hands-free voice mode'
                      : 'Enable hands-free voice mode (speak → AI responds → listen again)'
                  }
                >
                  <i className={`text-base ${isVoiceLoop ? 'ri-customer-service-2-fill' : 'ri-customer-service-2-line'}`} />
                  {isVoiceLoop && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#008540] animate-ping" />
                  )}
                </button>
              )}

              <textarea
                ref={textareaRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                onPaste={handleImagePaste}
                placeholder={
                  isListening
                    ? 'Speak now… I will auto-submit when you pause'
                    : pandoyPlaceholder(
                        user?.role,
                        Boolean(fieldContext),
                        user?.monitoring_area
                      )
                }
                className={`flex-1 min-w-0 bg-transparent border-0 text-[13.5px] resize-none focus:outline-none focus:ring-0 overflow-hidden ${
                  isDark
                    ? 'text-[#e6edf3] placeholder:text-[#6e7681]'
                    : 'text-[#111827] placeholder:text-[#6b7280]'
                }`}
                rows={1}
                style={{ minHeight: '24px', maxHeight: '120px' }}
              />
              <button
                type="button"
                onClick={handleSendMessage}
                disabled={isLoading || isCapturingDashboard || (!inputText.trim() && !attachedImage)}
                className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                  isLoading || (!inputText.trim() && !attachedImage)
                    ? pd.sendOff
                    : pd.sendOn
                }`}
                aria-label="Send message"
              >
                {isLoading ? (
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  <i className="ri-send-plane-fill text-[15px]" />
                )}
              </button>
            </div>
            {/* Voice listening status indicator */}
            {isListening && (
              <div
                className={`flex items-center justify-center gap-2 mt-2 text-[11px] ${
                  isDark ? 'text-red-400' : 'text-red-500'
                }`}
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                </span>
                <span className="font-medium">Listening{isVoiceLoop ? ' (Voice Loop)' : ''}…</span>
                <span className="inline-flex items-center gap-[1.5px] h-3">
                  <span className="voice-wave-bar h-2 w-[2px] rounded-full" />
                  <span className="voice-wave-bar h-3 w-[2px] rounded-full" style={{ animationDelay: '0.15s' }} />
                  <span className="voice-wave-bar h-2 w-[2px] rounded-full" style={{ animationDelay: '0.3s' }} />
                  <span className="voice-wave-bar h-3 w-[2px] rounded-full" style={{ animationDelay: '0.45s' }} />
                  <span className="voice-wave-bar h-2 w-[2px] rounded-full" style={{ animationDelay: '0.6s' }} />
                </span>
              </div>
            )}
            <p
              className={`text-[11px] leading-snug text-center mt-2 ${isDark ? 'text-gray-500' : 'text-[#6b8577]'}`}
            >
              {GPT_DISCLAIMER_TEXT}
            </p>
          </div>
            </div>
          </div>
        </div>
      )}

      {/* Full-screen image viewer */}
      {fullScreenImage && (
        <div
          data-digisaka-gpt-ui
          className="fixed inset-0 z-[96] bg-black/80 flex items-center justify-center"
          onClick={() => setFullScreenImage(null)}
        >
          <div
            className="relative max-w-full max-h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={fullScreenImage}
              alt="Full-size AI image"
              className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
            <button
              type="button"
              onClick={() => setFullScreenImage(null)}
              className="absolute top-2 right-2 bg-black/70 text-white rounded-full w-9 h-9 flex items-center justify-center hover:bg-black/90"
              aria-label="Close image"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

